import { validateCorrectionSemanticBinding } from '../audit/semanticFactBinding';
/**
 * Deterministic source-supported audit correction without a full AI rerun.
 *
 * Applies one or more explicit source-supported factual corrections to a
 * `SiteRagReport`, revalidates affected findings and truth gates against the
 * frozen `SiteEvidenceDocument`, clears ONLY holds genuinely resolved by the
 * correction while preserving unrelated holds and real evidence gaps, records
 * the correction in the versioned `approvedFactualCorrections` ledger, and
 * mints a new immutable `ReportDocumentModelV1`, `PresentationSnapshotV1`,
 * `ReportRevisionReferenceV1`, `DecisionAnalysisSnapshotV1`, and
 * `AnalysisRevisionReferenceV1`.
 */

import type {
  AnalysisRevisionReferenceV1,
  DecisionAnalysisSnapshotV1,
} from '../../types/decisionAnalysis';
import {
  reportRevisionReferenceV1Schema,
  type PresentationSnapshotV1,
  type ReportDocumentModelV1,
  type ReportRevisionReferenceV1,
} from '../../types/reportContractsV2';
import type { SiteAuditOverride, SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SanityFinding, SiteDocument, SiteRagReport } from '../../types/siteRag';
import {
  absenceSentenceIdentity,
  classifySentenceForTest,
  extractAbsenceAssertionCandidates,
} from '../audit/absenceAssertionDetector';
import {
  buildTruthQuestions,
  collectTruthBlockingRows,
  summarizeQaSeverity,
} from '../audit/qaSeverityPolicy';
import { isFalsePositiveQueueDurationFindingText } from '../audit/queueDurationCheck';
import { reevaluateStampedHoldFindings } from '../audit/reevaluateQualityHold';
import { correctUnsupportedVerifiedMarkers, RELIABILITY_MARKER_CORRECTION_EXCLUDED_KEYS } from '../audit/reliabilityMarkerCorrection';
import { applyCaveatHonestyGate } from '../audit/caveatHonestyVerifier';
import { sourceRoleReviewFields } from '../audit/sourceRoleTruthGuard';
import { buildSourceRoleSentenceRepair } from '../audit/sourceRoleSentenceRepair';
import { buildSourceRolePlanningRepair } from '../audit/sourceRolePlanningRepair';
import { reconcileReportVerdictProseWithConclusion } from './reconcileReportVerdictProse';
import { deriveScreeningRecommendationSetV1 } from '../findings/screeningArchitectureRecommendations';
import { runFrozenReportFactCheck } from '../../services/reportFactCheck';
import { reportFullText } from '../audit/reportTextExtract';
import {
  extractOffendingToken,
  filterSurvivingReviewIssues,
  isPhaseCumulativeAdditionComplaint,
  isReviewIssueAlreadyQualifiedInText,
} from '../audit/reviewIssueSurvival';
import { attestedCheckIds } from '../audit/truthAttestations';
import { runReportSanityCheckPass } from '../reportSanityChecks';
import { buildDecisionAnalysisFromReportV1 } from '../decisionAnalysis/fromReport';
import { buildAnalysisRevisionReferenceV1 } from '../decisionAnalysis/revision';
import {
  buildReportSedMaterialDependencyManifest,
  computeSedContentHashV2,
} from '../decisionAnalysis/sedContentIdentity';
import { campusResourceAdvisoryNumericStatements } from '../findings/campusResourceAdvisory';
import { buildCanonicalFactStore, liveSiteContextClaims } from '../grounding/buildCanonicalFactStore';
import {
  createCanonicalFactStore,
  type CanonicalFact,
} from '../grounding/canonicalFacts';
import {
  runGroundingGate,
  type GroundingViolation,
  type RunGroundingGateResult,
} from '../grounding/groundingValidator';
import type { FrozenModeledProjectBudgetSource } from '../grounding/modeledProjectBudget';
import { buildNumericSourceEvidence } from '../grounding/numericSourceEvidence';
import { freezeDesignLoadMarginSources } from '../grounding/sourceStatedDesignLoadMargin';
import { getAllKnowledgeEntries } from '../../services/knowledge/peerReviewDb';
import {
  isVerifiedSanityCategory,
  upsertApprovedFactualCorrection,
} from '../preservedUserDecisions';
import {
  overlaySanityDecisionMap,
  sanityFindingIdentity,
} from '../reportUserState';
import {
  buildPresentationSnapshotV1,
  buildReportDocumentModelV1,
} from './buildReportDocumentV1';
import { evaluateReportDelivery } from './evaluateReportDelivery';
import {
  finalTruthRoots,
  finalTruthRowKey,
  finalTruthSurface,
  measureFinalTruth,
} from './finalTruthReconcile';
import { resolveQualityHold } from './qualityHold';
import { hashCanonicalJson } from './reportContractHash';
import {
  collectNarrativeSections,
  detectClaimConflicts,
  isContextOnlyQueueClaim,
  isNonHeadroomGridCapacityClaim,
} from '../../services/claimExtraction';
import {
  deduplicateCanonicalReferences,
  renumberReportReferences,
} from '../renumberReferences';

export interface SourceSupportedAuditCorrectionInput {
  /** Dot-separated field path on `SiteRagReport` (e.g. `powerAnalysis`, `summary`). */
  fieldPath: string;
  /** Exact substring inside the target field to replace (optional if replacing whole field). */
  targetSubstring?: string;
  /** Replacement prose supported by the cited source/SED evidence. */
  replacementText: string;
  /** Non-empty source citation or SED fact path backing the correction. */
  evidenceCitation: string;
  /** Optional SED fact paths anchored to this correction for future rerun reconciliation. */
  anchorFactPaths?: readonly string[];
  /** Optional sanity finding identities or substrings resolved by this correction. */
  targetSanityFindingMatches?: readonly string[];
  /** Optional explicit sanity finding identity resolved by this correction. */
  sanityFindingIdentity?: string;
  /** Optional finding category (acknowledgements such as `risk-verified` / `strength-verified` are rejected). */
  category?: SanityFinding['category'];
  /** Optional resolution state (`ignore` and `withdrawn` are rejected as non-applicable). */
  resolution?: SanityFinding['resolution'] | 'withdrawn';
  /** Optional structured source fields backing the correction for conflict detection across reruns. */
  evidenceSourceFields?: Record<string, string | number | boolean | null>;
  /** Optional source document IDs backing the correction. */
  evidenceSourceIds?: readonly string[];
  /** Optional SED or source revision backing the correction. */
  evidenceSourceRevision?: string | number;
  /** Optional section label for the durable correction ledger. */
  section?: string;
  /** Optional operator note explaining the correction. */
  userNote?: string;
}

export interface ApplySourceSupportedAuditCorrectionArgs {
  report: SiteRagReport;
  sed: SiteEvidenceDocument;
  corrections: readonly SourceSupportedAuditCorrectionInput[];
  /** Optional durable user state to overlay before evaluating/applying corrections. */
  userState?: unknown;
  siteId?: string;
  ownerUid?: string;
  siteOwnerUid?: string;
  site?: Partial<SiteEntity> | null;
  frozenPlanningDocuments?: readonly SiteDocument[];
  auditOverrides?: SiteAuditOverride[];
  runGate?: (report: SiteRagReport) => RunGroundingGateResult;
  appliedAt?: string;
  appliedByUid?: string;
  revalidateOnly?: boolean;
}

export interface SourceSupportedAuditCorrectionReceiptV1 {
  schemaVersion: 1;
  appliedAt: string;
  appliedByUid?: string;
  sedId: string;
  sedVersion: number;
  previousRevisionId?: string;
  newRevisionId: string;
  corrections: Array<{
    fieldPath: string;
    evidenceCitation: string;
    beforeHash: string;
    afterHash: string;
  }>;
  clearedHoldFindings: string[];
  remainingHoldFindings: string[];
}

export type ApplySourceSupportedAuditCorrectionResult =
  | {
      applied: false;
      reason:
        | 'empty-corrections'
        | 'missing-evidence-citation'
        | 'unverified-source-evidence'
        | 'forbidden-field-path'
        | 'target-field-not-found'
        | 'target-substring-not-found'
        | 'no-effective-change'
        | 'introduces-new-truth-violation'
        | 'introduces-new-absence-assertion'
        | 'correction-withdrawn'
        | 'acknowledgement-not-factual-correction'
        | 'diagnostic-complaint-not-replacement'
        | 'ambiguous-field-path'
        | 'wrong-field-mapping';
      detail?: string;
    }
  | {
      applied: true;
      report: SiteRagReport;
      /** Exact canonical frozen source report payload hashed into `reportRevision.sourceHash` and stored at `reportRevision.sourcePath`. */
      sourceReport: SiteRagReport;
      /** Exact canonical immutable body payload stored at `bodyPath`. */
      bodyReport: SiteRagReport;
      bodyHash: string;
      bodyPath: string;
      document: ReportDocumentModelV1;
      presentation: PresentationSnapshotV1;
      reportRevision: ReportRevisionReferenceV1 & { sourcePath: string; sourceHash: string };
      decisionAnalysis: DecisionAnalysisSnapshotV1;
      analysisRevision: AnalysisRevisionReferenceV1;
      previousRevisionId?: string;
      newRevisionId: string;
      clearedHoldFindings: string[];
      remainingHoldFindings: string[];
      receipt: SourceSupportedAuditCorrectionReceiptV1;
      /** Set when `revalidateOnly === true` and zero canonical audit/hold/truth/SED state changed. */
      noOpRevalidate?: boolean;
    };

const FORBIDDEN_ROOT_FIELDS = new Set<string>([
  'id',
  'siteId',
  'sedId',
  'sedVersion',
  'reportType',
  'reportDocument',
  'presentationSnapshot',
  'reportRevision',
  'decisionAnalysis',
  'analysisRevision',
  'analysisReadiness',
  'deterministicScreeningValidation',
  'qaFindings',
  'qualityHold',
  'qualityHoldFindings',
]);

function cleanJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isForbiddenFieldPath(fieldPath: string): boolean {
  const trimmed = fieldPath.trim();
  if (!trimmed) return true;
  const parts = trimmed.replace(/\[(\d+)\]/g, '.$1').split('.');
  if (parts.some((p) => !p || ['__proto__', 'constructor', 'prototype'].includes(p))) {
    return true;
  }
  const root = parts[0]!;
  if (
    FORBIDDEN_ROOT_FIELDS.has(root) ||
    RELIABILITY_MARKER_CORRECTION_EXCLUDED_KEYS.has(root) ||
    /AtGen$/.test(root)
  ) {
    return true;
  }
  return parts.some((p) => RELIABILITY_MARKER_CORRECTION_EXCLUDED_KEYS.has(p) || /AtGen$/.test(p));
}

function readStringAtPath(target: unknown, fieldPath: string): string | null {
  const keys = fieldPath.replace(/\[(\d+)\]/g, '.$1').split('.');
  let node: unknown = target;
  for (const key of keys) {
    if (!node || typeof node !== 'object' || !Object.hasOwn(node, key)) return null;
    node = (node as Record<string, unknown>)[key];
  }
  return typeof node === 'string' ? node : null;
}

function writeStringAtPath(target: unknown, fieldPath: string, nextValue: string): boolean {
  const keys = fieldPath.replace(/\[(\d+)\]/g, '.$1').split('.');
  let node: unknown = target;
  for (const key of keys.slice(0, -1)) {
    if (!node || typeof node !== 'object' || !Object.hasOwn(node, key)) return false;
    node = (node as Record<string, unknown>)[key];
  }
  const lastKey = keys.at(-1);
  if (!lastKey || !node || typeof node !== 'object' || !Object.hasOwn(node, lastKey)) return false;
  if (typeof (node as Record<string, unknown>)[lastKey] !== 'string') return false;
  (node as Record<string, unknown>)[lastKey] = nextValue;
  return true;
}

const SECTION_DEFAULT_FIELD_PATHS: Record<string, readonly string[]> = {
  overview: ['summary', 'executiveSummary', 'projectIntentOverview'],
  summary: ['summary', 'executiveSummary'],
  'executive summary': ['executiveSummary', 'summary'],
  'exec-summary': ['executiveSummary', 'summary'],
  power: [
    'powerAnalysis',
    'powerAnalysis.substationAssessment',
    'powerAnalysis.capacityAnalysis',
    'powerAnalysis.interconnectionQueue',
    'insights.powerStrategy',
  ],
  'power & grid': [
    'powerAnalysis',
    'powerAnalysis.substationAssessment',
    'powerAnalysis.capacityAnalysis',
    'insights.powerStrategy',
  ],
  poweranalysis: [
    'powerAnalysis',
    'powerAnalysis.substationAssessment',
    'powerAnalysis.capacityAnalysis',
    'insights.powerStrategy',
  ],
  'grid power study': [
    'gridStudy.recommendations',
    'gridStudy.interconnectionAssessment',
    'gridStudy.transmissionAccess',
    'gridStudy.capacityAnalysis',
    'powerAnalysis',
    'powerAnalysis.interconnectionQueue',
    'insights.powerStrategy',
  ],
  'grid power study — recommendations': [
    'gridStudy.recommendations',
    'gridStudy.interconnectionAssessment',
    'gridStudy.transmissionAccess',
    'gridStudy.capacityAnalysis',
    'powerAnalysis',
    'powerAnalysis.interconnectionQueue',
    'insights.powerStrategy',
  ],
  'grid power study - recommendations': [
    'gridStudy.recommendations',
    'gridStudy.interconnectionAssessment',
    'gridStudy.transmissionAccess',
    'gridStudy.capacityAnalysis',
    'powerAnalysis',
    'powerAnalysis.interconnectionQueue',
    'insights.powerStrategy',
  ],
  'grid power study -> recommendations': [
    'gridStudy.recommendations',
    'gridStudy.interconnectionAssessment',
    'gridStudy.transmissionAccess',
    'gridStudy.capacityAnalysis',
    'powerAnalysis',
    'powerAnalysis.interconnectionQueue',
    'insights.powerStrategy',
  ],
  gridstudy: [
    'gridStudy.recommendations',
    'gridStudy.interconnectionAssessment',
    'gridStudy.transmissionAccess',
    'gridStudy.capacityAnalysis',
  ],
  water: [
    'waterAnalysis',
    'waterAnalysis.supplyAvailability',
    'waterAnalysis.waterRightsAnalysis',
    'insights.coolingStrategy',
  ],
  'water & cooling': [
    'waterAnalysis',
    'waterAnalysis.supplyAvailability',
    'insights.coolingStrategy',
  ],
  wateranalysis: [
    'waterAnalysis',
    'waterAnalysis.supplyAvailability',
    'insights.coolingStrategy',
  ],
  cooling: ['insights.coolingStrategy', 'waterAnalysis.coolingWaterRequirements'],
  regulatory: ['insights.regulatoryOutlook', 'alternativesAnalysis'],
  commercial: ['insights.commercialViability', 'bopEconomics', 'costRangeEstimate'],
  'test-fit': ['testFitAnalysis.siteConstraints', 'constructabilityAssessment'],
  developmentpathway: ['developmentPathway'],
  'development pathway': ['developmentPathway'],
  'grid power study — islanding readiness': [
    'gridStudy.islandingReadiness',
    'gridStudy.interconnectionAssessment',
    'gridStudy.capacityAnalysis',
    'gridStudy.recommendations',
  ],
  'grid power study - islanding readiness': [
    'gridStudy.islandingReadiness',
    'gridStudy.interconnectionAssessment',
    'gridStudy.capacityAnalysis',
    'gridStudy.recommendations',
  ],
};

function expandReportCandidatePath(report: SiteRagReport, pathExpr: string): string[] {
  const normalizedExpr = pathExpr.trim().replace(/\[(\d+)\]/g, '.$1');
  if (!normalizedExpr) return [];
  if (readStringAtPath(report, normalizedExpr) !== null) return [normalizedExpr];
  const keys = normalizedExpr.split('.');
  let node: unknown = report;
  for (const key of keys) {
    if (!node || typeof node !== 'object' || !Object.hasOwn(node, key)) return [];
    node = (node as Record<string, unknown>)[key];
  }
  if (Array.isArray(node)) {
    const out: string[] = [];
    node.forEach((item, idx) => {
      const elemPath = `${normalizedExpr}.${idx}`;
      if (readStringAtPath(report, elemPath) !== null) {
        out.push(elemPath);
      } else if (item && typeof item === 'object' && !Array.isArray(item)) {
        for (const [k, v] of Object.entries(item as Record<string, unknown>)) {
          if (typeof v === 'string' && k === 'description') {
            out.push(`${elemPath}.${k}`);
          }
        }
      }
    });
    return out;
  }
  if (node && typeof node === 'object') {
    const out: string[] = [];
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      if (typeof v === 'string') {
        out.push(`${normalizedExpr}.${k}`);
      }
    }
    return out;
  }
  return [];
}

const TOP_LEVEL_REPORT_FIELD_FINDING_RE =
  /\b(executiveSummary|projectIntentOverview|powerAnalysis|connectivityAnalysis|environmentalAnalysis|sitingNotes|btmDecisionSummary)\b/g;

/**
 * Extracts explicit dotted or bracketed report field paths mentioned in a
 * reviewer or sanity finding string (e.g. `developmentPathway[3].description`
 * or `gridStudy.interconnectionAssessment and gridStudy.capacityAnalysis`).
 */
export function extractExplicitFieldPathsFromFinding(
  report: SiteRagReport,
  findingText?: string,
): string[] {
  if (typeof findingText !== 'string' || !findingText.trim()) return [];
  const out: string[] = [];
  const pushResolved = (rawExpr: string) => {
    const clean = rawExpr.replace(/[.,;:)\]'"”’]+$/, '').replace(/\[(\d+)\]/g, '.$1');
    if (!clean || /^sed\./i.test(clean)) return;
    const expanded = expandReportCandidatePath(report, clean);
    if (expanded.length === 1) {
      if (!out.includes(expanded[0]!)) out.push(expanded[0]!);
    } else if (readStringAtPath(report, clean) !== null) {
      if (!out.includes(clean)) out.push(clean);
    }
  };

  for (const match of findingText.matchAll(/\b([A-Za-z][A-Za-z0-9_]*(?:\[\d+\]|\.[A-Za-z0-9_]+)+)\b/g)) {
    pushResolved(match[1]!);
  }
  for (const match of findingText.matchAll(TOP_LEVEL_REPORT_FIELD_FINDING_RE)) {
    const fieldName = match[1]!;
    if (readStringAtPath(report, fieldName) !== null && !out.includes(fieldName)) {
      out.push(fieldName);
    }
  }
  return out;
}

export function listCandidateFieldPathsForFinding(
  report: SiteRagReport,
  finding: Pick<SanityFinding, 'section' | 'finding'>,
): Array<{ path: string; currentText: string }> {
  const explicit = extractExplicitFieldPathsFromFinding(report, finding.finding);
  const paths: string[] = [...explicit];
  if (paths.length === 0 && finding.section) {
    const rawParts = finding.section
      .split(/\s*\/\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
    for (const part of rawParts) {
      const normKey = part.toLowerCase();
      const mapped = SECTION_DEFAULT_FIELD_PATHS[normKey];
      if (mapped) {
        for (const p of mapped) {
          for (const exp of expandReportCandidatePath(report, p)) {
            if (!paths.includes(exp)) paths.push(exp);
          }
        }
      } else {
        for (const exp of expandReportCandidatePath(report, part)) {
          if (!paths.includes(exp)) paths.push(exp);
        }
      }
    }
  }
  return paths
    .map((path) => {
      const currentText = readStringAtPath(report, path);
      return currentText !== null ? { path, currentText } : null;
    })
    .filter((entry): entry is { path: string; currentText: string } => entry !== null);
}

function sectionFamilyForPathOrLabel(raw: string): string | null {
  const s = raw.trim().toLowerCase().replace(/\[(\d+)\]/g, '.$1');
  if (!s) return null;
  if (s.startsWith('developmentpathway') || s.includes('development pathway')) return 'developmentPathway';
  if (s.startsWith('wateranalysis') || s === 'water' || s.startsWith('water ') || s.includes('water & cooling')) return 'water';
  if (
    s.startsWith('gridstudy') ||
    s.startsWith('poweranalysis') ||
    s.startsWith('insights.powerstrategy') ||
    s === 'power' ||
    s.startsWith('power ') ||
    s.includes('grid power study')
  ) {
    return 'powerGrid';
  }
  if (
    s === 'summary' ||
    s === 'executivesummary' ||
    s === 'executive summary' ||
    s === 'exec-summary' ||
    s === 'overview' ||
    s.startsWith('executive summary (')
  ) {
    return 'summary';
  }
  return null;
}

export function areSectionAndFieldPathCompatible(
  section: string | undefined,
  fieldPath: string,
): boolean {
  const cleanSection = (section ?? '').trim();
  const cleanField = fieldPath.trim().replace(/\[(\d+)\]/g, '.$1');
  if (!cleanSection || !cleanField) return true;
  if (cleanSection.replace(/\[(\d+)\]/g, '.$1') === cleanField) return true;
  const sectionParts = cleanSection
    .split(/\s*\/\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  const fieldFamily = sectionFamilyForPathOrLabel(cleanField);
  if (!fieldFamily) return true;
  const candidateFamilies = sectionParts
    .map((p) => sectionFamilyForPathOrLabel(p))
    .filter((f): f is string => f !== null);
  if (candidateFamilies.length === 0) return true;
  return candidateFamilies.includes(fieldFamily);
}

/**
 * Detects when replacementText is actually a diagnostic audit/reviewer
 * complaint or editorial meta-instruction rather than replacement report prose.
 */
export function isDiagnosticComplaintNotReplacement(
  replacementText: string,
  findingTexts: readonly string[] = [],
): { isComplaint: boolean; detail?: string } {
  const trimmed = (replacementText ?? '').trim();
  if (!trimmed) {
    return { isComplaint: true, detail: 'Replacement text cannot be empty.' };
  }
  const normReplacement = normalizeForLookup(trimmed);
  for (const ft of findingTexts) {
    if (typeof ft !== 'string' || ft.trim().length < 12) continue;
    const normFinding = normalizeForLookup(ft);
    if (normFinding && normReplacement === normFinding) {
      return {
        isComplaint: true,
        detail:
          'Replacement text repeats the audit finding diagnostic complaint rather than providing source-supported replacement prose.',
      };
    }
  }
  if (
    /^In\s+[A-Za-z0-9_.[\]-]+(?:\s+and\s+[A-Za-z0-9_.[\]-]+)?(?:\s*\([^)]*\))?,\s+(?:the\s+text|Phase\s+\d+|the\s+narrative|the\s+section|it)\b/i.test(
      trimmed,
    ) ||
    /^The\s+(?:Executive\s+Summary|report|section|subsection|narrative|text|table|study|summary|recommendation)\s+(?:repeats|states|claims|asserts|omits|lacks|fails\s+to|names|identifies|cites|describes|lists|incorrectly)\b.*?\b(?:without|rather\s+than|instead\s+of|which\s+contradicts|contradicting)\b/i.test(
      trimmed,
    ) ||
    /\bwithout\s+(?:hedging\s+it\s+as|explicitly\s+stating|their\s+full\s+cumulative|qualifying\s+it\s+as|stating\s+their)\b/i.test(
      trimmed,
    ) ||
    /\bare\s+stated\s+without\s+their\b/i.test(trimmed) ||
    /\brather\s+than\s+framing\s+them\s+as\s+conditional\b/i.test(trimmed) ||
    /\bas\s+required\s+by\s+the\s+(?:phasing|prompt|grounding|screening)\s+rules\b/i.test(trimmed) ||
    /^(?:Retain\s+(?:High|Medium|Low)\s+severity|No\s+(?:further\s+)?changes?\s+(?:needed|required)|Inspect\s+the\s+section\s+narrative|Rerun\s+the\s+report\s+quality\s+audit|Verification\s+of\s+the\s+exact\b|Confirmed\s+(?:by|as)\b|Automated\s+repair\s+could\s+not\s+reconcile)/i.test(
      trimmed,
    ) ||
    /^(?:Qualify\s+the\b|Remove\s+(?:or\s+qualify|the\s+unsupported|the\s+unverified)|Clarify\s+that\b|Explicitly\s+(?:state|qualify|disclose|label)\b|Update\s+the\s+(?:text|section|narrative|report|summary|table)\b|Revise\s+the\s+(?:text|section|narrative|report|summary|table)\b|Replace\s+the\s+(?:unsupported|unverified|text|claim)\b|Correct\s+the\s+(?:text|section|narrative|report|summary|table)\b|Align\s+the\s+(?:text|section|narrative|report|summary|table)\b|Ensure\s+(?:that\s+)?the\s+(?:text|section|narrative|report|summary|table)\b)/i.test(
      trimmed,
    ) ||
    /^(?:⚠\s*HOLD\s*[—-]\s*UNRESOLVED\s+CONTRADICTION:|Internal\s+contradiction:|Calculation\s+inconsistency:|Audit\s+finding\s+noted\s*\(|The\s+report\s+contains\s+conflicting\s+(?:figures|statements|claims|numbers))/i.test(
      trimmed,
    )
  ) {
    return {
      isComplaint: true,
      detail:
        'Replacement text is a diagnostic audit complaint or editorial review note, not source-supported replacement narrative prose.',
    };
  }
  return { isComplaint: false };
}

/**
 * Resolves a caller-supplied `fieldPath` or `SanityFinding.section` label to a
 * concrete string-valued dot-path on `report`. If `fieldPathOrSection` already
 * resolves directly to a string on `report` (or is a forbidden path), returns
 * it unchanged so validation handles it deterministically.
 */
export function resolveReportFieldPathForCorrection(
  report: SiteRagReport,
  fieldPathOrSection?: string,
  findingText?: string,
  targetSubstring?: string,
): string {
  const raw = (fieldPathOrSection ?? '').trim();
  const normalizedRaw = raw.replace(/\[(\d+)\]/g, '.$1');
  if (normalizedRaw && isForbiddenFieldPath(normalizedRaw)) {
    return normalizedRaw;
  }

  const explicitPaths = extractExplicitFieldPathsFromFinding(report, findingText);
  if (explicitPaths.length > 1) {
    const rawIsExplicitLeaf = normalizedRaw.length > 0 && explicitPaths.includes(normalizedRaw);
    if (!rawIsExplicitLeaf) {
      if (targetSubstring && targetSubstring.trim().length > 0) {
        const subMatches = explicitPaths.filter((p) => {
          const val = readStringAtPath(report, p);
          return val !== null && val.includes(targetSubstring);
        });
        if (subMatches.length === 1) {
          return subMatches[0]!;
        }
      }
      return `ambiguous:${explicitPaths.join(',')}`;
    }
  }

  if (explicitPaths.length === 1) {
    const singleExplicit = explicitPaths[0]!;
    if (!normalizedRaw || areSectionAndFieldPathCompatible(normalizedRaw, singleExplicit)) {
      if (
        !normalizedRaw ||
        normalizedRaw === singleExplicit ||
        singleExplicit.startsWith(`${normalizedRaw}.`) ||
        readStringAtPath(report, normalizedRaw) === null
      ) {
        return singleExplicit;
      }
    } else {
      return `wrong-field:${normalizedRaw}!=${singleExplicit}`;
    }
  }

  if (normalizedRaw && readStringAtPath(report, normalizedRaw) !== null) {
    return normalizedRaw;
  }

  const addCandidatePaths = (pathExpr: string, into: string[]) => {
    for (const expanded of expandReportCandidatePath(report, pathExpr)) {
      if (!into.includes(expanded)) into.push(expanded);
    }
  };

  const directExpanded = normalizedRaw ? expandReportCandidatePath(report, normalizedRaw) : [];
  if (directExpanded.length === 1 && directExpanded[0] === normalizedRaw) {
    return normalizedRaw;
  }

  const candidatePaths: string[] = [...directExpanded];
  if (normalizedRaw && Object.hasOwn(report as object, normalizedRaw)) {
    const sub = (report as unknown as Record<string, unknown>)[normalizedRaw];
    if (sub && typeof sub === 'object' && !Array.isArray(sub)) {
      for (const [k, v] of Object.entries(sub as Record<string, unknown>)) {
        if (typeof v === 'string' || Array.isArray(v)) {
          addCandidatePaths(`${normalizedRaw}.${k}`, candidatePaths);
        }
      }
    }
  }

  const normalizedKey = normalizedRaw.toLowerCase();
  const mapped = SECTION_DEFAULT_FIELD_PATHS[normalizedKey] ?? [];
  const expandedMapped: string[] = [];
  for (const p of mapped) {
    addCandidatePaths(p, candidatePaths);
    addCandidatePaths(p, expandedMapped);
  }

  // Only include global fallback narrative paths when no specific section/field
  // was requested, preventing cross-section misrouting (e.g. developmentPathway -> waterAnalysis).
  const fallbackNarrativePaths = [
    'gridStudy.recommendations',
    'gridStudy.interconnectionAssessment',
    'gridStudy.transmissionAccess',
    'gridStudy.capacityAnalysis',
    'powerAnalysis',
    'powerAnalysis.substationAssessment',
    'powerAnalysis.capacityAnalysis',
    'powerAnalysis.interconnectionQueue',
    'waterAnalysis',
    'waterAnalysis.supplyAvailability',
    'waterAnalysis.waterRightsAnalysis',
    'summary',
    'executiveSummary',
    'insights.powerStrategy',
    'insights.coolingStrategy',
    'insights.regulatoryOutlook',
    'insights.commercialViability',
  ];
  if (!normalizedRaw || candidatePaths.length === 0) {
    const rawFamily = sectionFamilyForPathOrLabel(normalizedRaw);
    for (const p of fallbackNarrativePaths) {
      if (rawFamily && sectionFamilyForPathOrLabel(p) !== rawFamily) continue;
      addCandidatePaths(p, candidatePaths);
    }
  }

  if (targetSubstring && targetSubstring.trim().length > 0) {
    const matchBySub = candidatePaths.find((p) => {
      const val = readStringAtPath(report, p);
      return val !== null && val.includes(targetSubstring);
    });
    if (matchBySub) return matchBySub;
  }

  if (findingText) {
    const token = extractOffendingToken(findingText);
    if (token) {
      const matchByToken = candidatePaths.find((p) => {
        const val = readStringAtPath(report, p);
        return val !== null && val.toLowerCase().includes(token.toLowerCase());
      });
      if (matchByToken) return matchByToken;
    }
    const keywords = Array.from(
      new Set(
        findingText
          .toLowerCase()
          .split(/[^a-z0-9-]+/)
          .filter((w) => w.length >= 4 && !['grid', 'power', 'study', 'report', 'states', 'section', 'recommendations', 'claim', 'source', 'with'].includes(w)),
      ),
    );
    if (keywords.length > 0) {
      const pool = expandedMapped.length > 0 ? expandedMapped : candidatePaths;
      let bestPath: string | null = null;
      let bestScore = 0;
      for (const p of pool) {
        const val = readStringAtPath(report, p)?.toLowerCase() ?? '';
        const score = keywords.filter((kw) => val.includes(kw)).length;
        if (score > bestScore) {
          bestScore = score;
          bestPath = p;
        }
      }
      if (bestPath && bestScore >= 2) return bestPath;
    }
  }

  if (candidatePaths.length > 0 && normalizedRaw) {
    const directCandidate = candidatePaths.find(
      (p) => p.startsWith(`${normalizedRaw}.`) || expandedMapped.includes(p),
    );
    if (directCandidate) return directCandidate;
  }

  return normalizedRaw || raw;
}

function resolveSedPathValue(sed: SiteEvidenceDocument, rawPath: string): unknown {
  const cleanPath = rawPath.trim().replace(/^sed\./, '');
  if (!cleanPath) return undefined;
  let cur: unknown = sed;
  for (const part of cleanPath.split('.')) {
    if (cur == null || typeof cur !== 'object') {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function extractFactValuesFromSed(
  sed: SiteEvidenceDocument,
  paths: readonly string[],
): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  for (const rawPath of paths) {
    const cleanPath = rawPath.trim();
    if (!cleanPath) continue;
    const cur = resolveSedPathValue(sed, cleanPath);
    if (typeof cur === 'string' || typeof cur === 'number' || typeof cur === 'boolean') {
      out[cleanPath] = cur;
    } else if (cur == null) {
      out[cleanPath] = null;
    } else {
      out[cleanPath] = JSON.stringify(cur);
    }
  }
  return out;
}

function collectAllSedNumbers(value: unknown, out: Set<number> = new Set()): Set<number> {
  if (typeof value === 'number' && Number.isFinite(value)) {
    out.add(value);
  } else if (typeof value === 'string') {
    const matches = value.matchAll(/\b(\d+(?:,\d{3})*(?:\.\d+)?)\b/g);
    for (const m of matches) {
      const n = Number(m[1]!.replace(/,/g, ''));
      if (Number.isFinite(n)) out.add(n);
    }
  } else if (Array.isArray(value)) {
    for (const item of value) collectAllSedNumbers(item, out);
  } else if (value && typeof value === 'object') {
    for (const child of Object.values(value as Record<string, unknown>)) {
      collectAllSedNumbers(child, out);
    }
  }
  return out;
}

function extractUnitNumbersFromText(text: string): Array<{ value: number; unit: string; raw: string }> {
  const out: Array<{ value: number; unit: string; raw: string }> = [];
  const regex = /\b(\d+(?:,\d{3})*(?:\.\d+)?)\s*(MW|MVA|kW|kV|acres|ac|km|mi|ft|GPD|gpd|MGD|mgd|m3\/day|m³\/day|%)\b/gi;
  for (const match of text.matchAll(regex)) {
    const num = Number(match[1]!.replace(/,/g, ''));
    if (Number.isFinite(num)) {
      out.push({
        value: num,
        unit: match[2]!.toLowerCase(),
        raw: match[0]!,
      });
    }
  }
  return out;
}

function numbersMatchWithinTolerance(a: number, b: number, toleranceRatio = 0.005): boolean {
  if (a === b) return true;
  const denom = Math.max(Math.abs(a), Math.abs(b), 1e-9);
  return Math.abs(a - b) / denom <= toleranceRatio;
}

const KNOWN_SED_ROOT_KEYS = new Set<string>([
  'power',
  'water',
  'grid',
  'connectivity',
  'environmental',
  'flood',
  'zoning',
  'identity',
  'siteResearch',
  'economicEvidence',
  'publicSentiment',
  'fullProjectIntent',
  'cooling',
  'financialAssumptions',
]);

/**
 * Validates that a correction's citations, anchor fact paths, structured source
 * fields, source IDs, and SED identity resolve against the frozen SED and
 * report reference list. Requires at least one concrete verifiable source anchor.
 */
function verifyCorrectionSourceBinding(
  c: SourceSupportedAuditCorrectionInput,
  sed: SiteEvidenceDocument,
  report: SiteRagReport,
  matchedSanityFinding?: SanityFinding,
): { ok: true } | { ok: false; detail: string } {
  // 0. Verify that the loaded SED matches the report's bound SED identity, version, and contentHash.
  if (
    typeof report.sedId === 'string' &&
    report.sedId.trim().length > 0 &&
    typeof sed.id === 'string' &&
    sed.id.trim().length > 0 &&
    report.sedId.trim() !== sed.id.trim()
  ) {
    return {
      ok: false,
      detail: `Loaded SED id ("${sed.id}") does not match report.sedId ("${report.sedId}").`,
    };
  }
  if (
    typeof report.sedVersion === 'number' &&
    typeof sed.version === 'number' &&
    report.sedVersion !== sed.version
  ) {
    return {
      ok: false,
      detail: `Loaded SED version (${sed.version}) does not match report.sedVersion (${report.sedVersion}).`,
    };
  }
  const expectedSedHash = report.decisionAnalysis?.input?.sed?.contentHash;
  if (typeof expectedSedHash === 'string' && expectedSedHash.trim().length > 0) {
    const actualSedHash = computeSedContentHashV2(sed);
    if (actualSedHash !== expectedSedHash.trim()) {
      return {
        ok: false,
        detail: `Loaded SED contentHash ("${actualSedHash}") does not match report decisionAnalysis SED contentHash ("${expectedSedHash.trim()}").`,
      };
    }
  }

  let hasVerifiedSourceAnchor = false;
  const inferredRefFactIds: string[] = [];

  // 1. Unconditionally validate `[Ref N]` citations in evidenceCitation and replacementText
  // (including when report.references is empty or absent). A valid reference index by itself
  // does not establish source support unless bound to a matching source title, field, or SED path.
  const maxRef = Array.isArray(report.references) ? report.references.length : 0;
  const combinedRefText = `${c.evidenceCitation} ${c.replacementText}`;
  for (const match of combinedRefText.matchAll(/\[\s*Ref\s+(\d+(?:\s*,\s*\d+)*)\s*\]/gi)) {
    const nums = match[1]!
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n));
    if (nums.length === 0) {
      return {
        ok: false,
        detail: `Correction for "${c.fieldPath}" contains an invalid [Ref] citation.`,
      };
    }
    for (const refNum of nums) {
      if (maxRef === 0 || refNum < 1 || refNum > maxRef) {
        return {
          ok: false,
          detail: `Correction for "${c.fieldPath}" cites [Ref ${refNum}], which is outside report.references (1..${maxRef}).`,
        };
      }
      const refEntry = report.references?.[refNum - 1] as unknown as Record<string, unknown> | undefined;
      if (refEntry && typeof refEntry.field === 'string' && refEntry.field.trim()) {
        inferredRefFactIds.push(refEntry.field.trim());
      }
    }
  }

  // 2. Validate anchorFactPaths against the frozen SED.
  if (Array.isArray(c.anchorFactPaths) && c.anchorFactPaths.length > 0) {
    for (const rawPath of c.anchorFactPaths) {
      const cleanPath = rawPath.trim();
      if (!cleanPath) {
        return {
          ok: false,
          detail: `Correction for "${c.fieldPath}" contains an empty anchorFactPath.`,
        };
      }
      const val = resolveSedPathValue(sed, cleanPath);
      if (val === undefined || val === null) {
        return {
          ok: false,
          detail: `Correction for "${c.fieldPath}" anchors SED path "${cleanPath}", which is absent or null on the frozen SED.`,
        };
      }
      hasVerifiedSourceAnchor = true;
    }
  }

  // 3. Validate evidenceSourceFields against the frozen SED (never silently skipping non-dotted or unknown roots).
  if (c.evidenceSourceFields && typeof c.evidenceSourceFields === 'object') {
    for (const [key, expectedVal] of Object.entries(c.evidenceSourceFields)) {
      const cleanKey = key.trim().replace(/^sed\./, '');
      const rootKey = cleanKey.split('.')[0] ?? '';
      if (!cleanKey || !KNOWN_SED_ROOT_KEYS.has(rootKey)) {
        return {
          ok: false,
          detail: `Correction for "${c.fieldPath}" specifies evidenceSourceFields["${key}"], which is not a recognized SED path.`,
        };
      }
      const actualVal = resolveSedPathValue(sed, cleanKey);
      if (actualVal === undefined || actualVal === null) {
        return {
          ok: false,
          detail: `Correction for "${c.fieldPath}" specifies evidenceSourceFields["${key}"], which is absent on the frozen SED.`,
        };
      }
      if (typeof expectedVal === 'number' && typeof actualVal === 'number') {
        if (!numbersMatchWithinTolerance(expectedVal, actualVal)) {
          return {
            ok: false,
            detail: `Correction for "${c.fieldPath}" specifies ${key}=${expectedVal}, contradicting frozen SED value ${actualVal}.`,
          };
        }
      } else if (
        expectedVal !== null &&
        (typeof actualVal === 'string' || typeof actualVal === 'number' || typeof actualVal === 'boolean') &&
        String(actualVal).trim().toLowerCase() !== String(expectedVal).trim().toLowerCase()
      ) {
        return {
          ok: false,
          detail: `Correction for "${c.fieldPath}" specifies ${key}="${String(expectedVal)}", contradicting frozen SED value "${String(actualVal)}".`,
        };
      }
      hasVerifiedSourceAnchor = true;
    }
  }

  // 4. Validate evidenceSourceRevision against sed.version when provided.
  if (
    c.evidenceSourceRevision !== undefined &&
    c.evidenceSourceRevision !== null &&
    sed.version !== undefined &&
    sed.version !== null &&
    String(c.evidenceSourceRevision).trim() !== '' &&
    String(c.evidenceSourceRevision).trim() !== String(sed.version).trim()
  ) {
    return {
      ok: false,
      detail: `Correction for "${c.fieldPath}" specifies evidenceSourceRevision=${String(c.evidenceSourceRevision)}, which does not match frozen SED version ${String(sed.version)}.`,
    };
  }

  // 5. Validate evidenceSourceIds against sed.uploadedDocuments / report.references / sed.id.
  if (Array.isArray(c.evidenceSourceIds) && c.evidenceSourceIds.length > 0) {
    for (const rawId of c.evidenceSourceIds) {
      const trimmedId = String(rawId ?? '').trim();
      if (!trimmedId) {
        return {
          ok: false,
          detail: `Correction for "${c.fieldPath}" contains an empty evidenceSourceId.`,
        };
      }
      const lowerId = trimmedId.toLowerCase();
      const matchesDoc =
        Array.isArray(sed.uploadedDocuments) &&
        sed.uploadedDocuments.some((doc) => {
          if (!doc || typeof doc !== 'object') return false;
          const d = doc as unknown as Record<string, unknown>;
          return [d.id, d.docId, d.parentDocId, d.documentId, d.name, d.fileName, d.title].some(
            (v) => typeof v === 'string' && v.trim().toLowerCase() === lowerId,
          );
        });
      const matchesRef =
        Array.isArray(report.references) &&
        report.references.some((ref) => {
          if (!ref || typeof ref !== 'object') return false;
          const r = ref as unknown as Record<string, unknown>;
          return [r.id, r.title, r.source, r.field, r.url].some(
            (v) => typeof v === 'string' && v.trim().toLowerCase().includes(lowerId),
          );
        });
      const matchesSedId =
        (typeof sed.id === 'string' &&
          (sed.id.trim().toLowerCase() === lowerId ||
            lowerId.startsWith(`${sed.id.trim().toLowerCase()}#`))) ||
        (typeof sed.version === 'number' &&
          lowerId.startsWith(`sed-v${sed.version}#`));
      const matchesFindingSourceId =
        Array.isArray(matchedSanityFinding?.evidenceSourceIds) &&
        matchedSanityFinding.evidenceSourceIds.some(
          (id) => typeof id === 'string' && id.trim().toLowerCase() === lowerId,
        );
      if (!matchesDoc && !matchesRef && !matchesSedId && !matchesFindingSourceId) {
        return {
          ok: false,
          detail: `Correction for "${c.fieldPath}" specifies evidenceSourceId "${trimmedId}", which is not present in frozen SED uploadedDocuments or report.references.`,
        };
      }
      hasVerifiedSourceAnchor = true;
    }
  }

  // 6. Check matchedSanityFinding structured source anchors when not already anchored.
  if (!hasVerifiedSourceAnchor && matchedSanityFinding?.evidenceSourceFields) {
    const entries = Object.entries(matchedSanityFinding.evidenceSourceFields);
    if (entries.length > 0) {
      let allMatched = true;
      for (const [key, expectedVal] of entries) {
        const cleanKey = key.trim().replace(/^sed\./, '');
        const actualVal = resolveSedPathValue(sed, cleanKey);
        if (actualVal === undefined || actualVal === null) {
          allMatched = false;
          break;
        }
        if (typeof expectedVal === 'number' && typeof actualVal === 'number') {
          if (!numbersMatchWithinTolerance(expectedVal, actualVal)) {
            allMatched = false;
            break;
          }
        } else if (
          expectedVal !== null &&
          String(actualVal).trim().toLowerCase() !== String(expectedVal).trim().toLowerCase()
        ) {
          allMatched = false;
          break;
        }
      }
      if (allMatched) {
        hasVerifiedSourceAnchor = true;
      }
    }
  }

  // 7. If still not anchored, check whether evidenceCitation matches a report reference,
  // an uploaded document, an explicit SED path, or the matched sanity finding's source/evidence.
  if (!hasVerifiedSourceAnchor) {
    const citLower = c.evidenceCitation.trim().toLowerCase();

    for (const pathMatch of c.evidenceCitation.matchAll(
      /\b(?:sed\.)?(power|water|grid|connectivity|environmental|flood|zoning|identity|siteResearch|economicEvidence|publicSentiment|fullProjectIntent|cooling|financialAssumptions)\.[A-Za-z0-9_.]+\b/g,
    )) {
      const resolved = resolveSedPathValue(sed, pathMatch[0]);
      if (resolved !== undefined && resolved !== null) {
        hasVerifiedSourceAnchor = true;
        break;
      }
    }

    if (!hasVerifiedSourceAnchor && Array.isArray(report.references)) {
      for (const ref of report.references) {
        if (!ref || typeof ref !== 'object') continue;
        const r = ref as unknown as Record<string, unknown>;
        for (const candidateVal of [r.title, r.source, r.field, r.value, r.url]) {
          if (typeof candidateVal === 'string' && candidateVal.trim().length >= 4) {
            const normRef = candidateVal.trim().toLowerCase();
            if (citLower.includes(normRef) || normRef.includes(citLower)) {
              hasVerifiedSourceAnchor = true;
              break;
            }
          }
        }
        if (hasVerifiedSourceAnchor) break;
      }
    }

    if (!hasVerifiedSourceAnchor && Array.isArray(sed.uploadedDocuments)) {
      for (const doc of sed.uploadedDocuments) {
        if (!doc || typeof doc !== 'object') continue;
        const d = doc as unknown as Record<string, unknown>;
        for (const candidateVal of [d.id, d.documentId, d.name, d.fileName, d.title]) {
          if (typeof candidateVal === 'string' && candidateVal.trim().length >= 4) {
            const normDoc = candidateVal.trim().toLowerCase();
            if (citLower.includes(normDoc) || normDoc.includes(citLower)) {
              hasVerifiedSourceAnchor = true;
              break;
            }
          }
        }
        if (hasVerifiedSourceAnchor) break;
      }
    }

    if (!hasVerifiedSourceAnchor && matchedSanityFinding) {
      const sfSource = (matchedSanityFinding as { source?: unknown }).source;
      for (const candidateVal of [matchedSanityFinding.evidence, sfSource]) {
        if (typeof candidateVal === 'string' && candidateVal.trim().length >= 4) {
          const normEv = candidateVal.trim().toLowerCase();
          if (citLower.includes(normEv) || normEv.includes(citLower)) {
            hasVerifiedSourceAnchor = true;
            break;
          }
        }
      }
    }
  }

  if (!hasVerifiedSourceAnchor) {
    return {
      ok: false,
      detail: `Correction for "${c.fieldPath}" has no verifiable source anchor in report.references, frozen SED paths, uploadedDocuments, or the matched audit finding.`,
    };
  }

  const semanticCheck = validateCorrectionSemanticBinding({
    fieldPath: c.fieldPath,
    targetSubstring: c.targetSubstring,
    replacementText: c.replacementText,
    evidenceCitation: c.evidenceCitation,
    anchorFactPaths: c.anchorFactPaths,
    evidenceSourceFields: c.evidenceSourceFields,
    inferredFactIds: inferredRefFactIds,
    resolveSedPathValue: (p) => resolveSedPathValue(sed, p),
  });
  if (!semanticCheck.ok) {
    return {
      ok: false,
      detail: semanticCheck.reason,
    };
  }

  return { ok: true };
}

function flattenUnknownStrings(value: unknown, out: string[] = []): string[] {
  if (typeof value === 'string') {
    if (value.trim()) out.push(value);
  } else if (Array.isArray(value)) {
    for (const item of value) flattenUnknownStrings(item, out);
  } else if (value && typeof value === 'object') {
    for (const child of Object.values(value as Record<string, unknown>)) {
      flattenUnknownStrings(child, out);
    }
  }
  return out;
}

function collectReportNarrativeBody(report: SiteRagReport): string {
  const full = reportFullText(report);
  const surface = finalTruthSurface(report, finalTruthRoots(report));
  const extra = flattenUnknownStrings(surface).join('\n');
  return full ? `${full}\n${extra}` : extra;
}

function normalizeForLookup(value: string): string {
  return value
    .replace(/\[Ref\s+\d+(?:\s*,\s*\d+)*\]/gi, '')
    .replace(/[‘’“”]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function appearsInNormalizedBody(normalizedBody: string, anchor: unknown): boolean {
  if (typeof anchor !== 'string') return true;
  const needle = normalizeForLookup(anchor);
  if (needle.length < 4) return true;
  return normalizedBody.includes(needle);
}

function updateClaimsMatchingEdit(
  report: SiteRagReport,
  previousFieldText: string,
  nextFieldText: string,
  targetSubstring?: string,
  replacementText?: string,
): void {
  const strippedTarget = targetSubstring?.replace(/^<!--\s*confidence:\s*[^>]+?\s*-->\s*/i, '');
  const mutateClaimArray = (claims: unknown) => {
    if (!Array.isArray(claims)) return;
    for (const item of claims) {
      if (!item || typeof item !== 'object') continue;
      const claim = item as { text?: unknown };
      if (typeof claim.text !== 'string') continue;
      if (claim.text === previousFieldText) {
        claim.text = nextFieldText;
      } else if (targetSubstring && replacementText && claim.text.includes(targetSubstring)) {
        claim.text = claim.text.replaceAll(targetSubstring, replacementText);
      } else if (
        strippedTarget &&
        replacementText &&
        strippedTarget !== targetSubstring &&
        claim.text.includes(strippedTarget)
      ) {
        claim.text = claim.text.replaceAll(strippedTarget, replacementText);
      }
    }
  };
  mutateClaimArray(report.claims);
  mutateClaimArray((report as unknown as Record<string, unknown>).editorialClaims);
}

function pruneResolvedAbsenceRows(report: SiteRagReport): SiteRagReport {
  const body = normalizeForLookup(collectReportNarrativeBody(report));
  if (!body || !report.qaFindings) return report;

  const qa: Record<string, unknown> = { ...report.qaFindings };

  // Absence assertion findings whose sentence no longer appears in the body (and replacement was verified exempt).
  if (qa.absenceAssertionAudit && typeof qa.absenceAssertionAudit === 'object') {
    const absence = qa.absenceAssertionAudit as Record<string, unknown>;
    if (Array.isArray(absence.findings)) {
      const kept = absence.findings.filter((raw) => {
        if (!raw || typeof raw !== 'object') return false;
        const row = raw as Record<string, unknown>;
        const sentence = typeof row.sentenceText === 'string' ? row.sentenceText : '';
        if (!sentence) return true;
        return appearsInNormalizedBody(body, sentence);
      });
      if (kept.length !== absence.findings.length) {
        qa.absenceAssertionAudit = {
          ...absence,
          findings: kept,
        };
      }
    }
  }

  return {
    ...report,
    qaFindings: qa as SiteRagReport['qaFindings'],
  };
}

/**
 * Builds a mandatory source-bound grounding gate runner backed by `buildCanonicalFactStore`
 * and `runGroundingGate` against the frozen `SiteEvidenceDocument`.
 */
function buildSourceBoundGroundingRunner(args: {
  baselineReport: SiteRagReport;
  sed: SiteEvidenceDocument;
  corrections: readonly SourceSupportedAuditCorrectionInput[];
  siteId: string;
  ownerUid?: string;
  siteOwnerUid?: string;
  site?: Partial<SiteEntity> | null;
  frozenPlanningDocuments?: readonly SiteDocument[];
  appliedAt: string;
  customRunGate?: (report: SiteRagReport) => RunGroundingGateResult;
}): (targetReport: SiteRagReport) => RunGroundingGateResult {
  const {
    baselineReport,
    sed,
    corrections,
    siteId,
    ownerUid,
    siteOwnerUid,
    site,
    frozenPlanningDocuments,
    appliedAt,
    customRunGate,
  } = args;
  const reportType = (baselineReport.reportType ?? 'feasibility') as never;
  const effectiveOwnerUid =
    sed.demonstrationProvenance?.ownerUid ??
    (sed.financialAssumptions as { dataCenterModel?: { ownerUid?: string } } | undefined)?.dataCenterModel?.ownerUid ??
    siteOwnerUid ??
    (baselineReport as { _ownerUid?: string })._ownerUid ??
    site?.createdBy?.uid ??
    ownerUid;

  const baseStore = buildCanonicalFactStore({
    ctx: {
      site: {
        ...(site ?? {}),
        id: siteId,
        name: baselineReport.siteName ?? sed.siteName ?? site?.name ?? siteId,
        address: baselineReport.address ?? sed.location?.address ?? site?.address ?? '',
        location: {
          lat: sed.location?.lat ?? site?.location?.lat ?? 0,
          lng: sed.location?.lng ?? site?.location?.lng ?? 0,
        },
        projectIntent: sed.fullProjectIntent ?? baselineReport.projectIntentAtGen ?? site?.projectIntent,
        ...(effectiveOwnerUid ? { _ownerUid: effectiveOwnerUid } : {}),
      } as never,
      sed,
      reports: [baselineReport],
    },
    sed,
    reportType,
    coverageMode: sed.coverageMode,
    nowIso: appliedAt,
  });

  const priorViolations = Array.isArray(baselineReport.qaFindings?.grounding?.violations)
    ? (baselineReport.qaFindings.grounding.violations as GroundingViolation[])
    : [];
  const priorFactIds = new Set(
    priorViolations
      .map((v) => v.factId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0),
  );

  // Retain all resolved/present canonical facts plus any absent fact explicitly
  // implicated in prior grounding violations so partial test fixtures don't
  // synthesize unrelated absent-domain failures.
  const activeFacts: CanonicalFact[] = [];
  for (const fact of baseStore.facts.values()) {
    if (fact.status !== 'absent' || priorFactIds.has(fact.factId)) {
      activeFacts.push(fact);
    }
  }


  const activeStore = createCanonicalFactStore({
    siteId: baseStore.siteId,
    reportType: baseStore.reportType,
    coverageMode: baseStore.coverageMode,
    facts: activeFacts,
    contextEntities: baseStore.contextEntities,
  });

  // Collect all authorized numbers from the SED, active canonical store,
  // expected values on prior grounding violations, and non-violating baseline narrative.
  const authorizedNumbers = collectAllSedNumbers(sed);
  collectAllSedNumbers(baselineReport.projectIntentAtGen, authorizedNumbers);
  const effectiveIntent = (sed.fullProjectIntent ?? baselineReport.projectIntentAtGen) as
    | {
        pueValue?: number | null;
        pueTarget?: number | null;
        phases?: Array<{ capacityMW?: number | null; sequence?: number }>;
      }
    | undefined;
  if (effectiveIntent && Array.isArray(effectiveIntent.phases)) {
    const pueRaw = effectiveIntent.pueValue ?? effectiveIntent.pueTarget;
    const pue = typeof pueRaw === 'number' && Number.isFinite(pueRaw) && pueRaw > 0 ? pueRaw : null;
    const sortedPhases = effectiveIntent.phases
      .slice()
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    let prevCum = 0;
    for (const ph of sortedPhases) {
      const cum = typeof ph.capacityMW === 'number' && Number.isFinite(ph.capacityMW) ? ph.capacityMW : null;
      if (cum != null && cum > 0) {
        authorizedNumbers.add(cum);
        const inc = Math.max(0, Math.round((cum - prevCum) * 1000) / 1000);
        if (inc > 0) authorizedNumbers.add(inc);
        if (pue != null) {
          authorizedNumbers.add(Math.round((cum / pue) * 100) / 100);
          authorizedNumbers.add(Math.round(cum * pue * 100) / 100);
          if (inc > 0) {
            authorizedNumbers.add(Math.round((inc / pue) * 100) / 100);
            authorizedNumbers.add(Math.round(inc * pue * 100) / 100);
          }
        }
        prevCum = cum;
      }
    }
  }
  for (const fact of activeFacts) {
    if (typeof fact.value === 'number' && Number.isFinite(fact.value)) {
      authorizedNumbers.add(fact.value);
    }
    for (const n of fact.numericValues ?? []) {
      if (Number.isFinite(n)) authorizedNumbers.add(n);
    }
  }
  for (const v of priorViolations) {
    if (typeof v.expected === 'string') {
      for (const item of extractUnitNumbersFromText(v.expected)) {
        authorizedNumbers.add(item.value);
      }
      for (const m of v.expected.matchAll(/\b(\d+(?:,\d{3})*(?:\.\d+)?)\b/g)) {
        const n = Number(m[1]!.replace(/,/g, ''));
        if (Number.isFinite(n)) authorizedNumbers.add(n);
      }
    }
  }
  for (const sf of baselineReport.sanityFindings ?? []) {
    for (const text of [sf.suggestion, sf.evidence]) {
      if (typeof text === 'string') {
        for (const item of extractUnitNumbersFromText(text)) {
          authorizedNumbers.add(item.value);
        }
      }
    }
  }
  for (const qi of baselineReport.qualityReview?.issues ?? []) {
    for (const text of [qi.issue, (qi as { fix?: string }).fix, (qi as { evidence?: string }).evidence]) {
      if (typeof text === 'string') {
        for (const item of extractUnitNumbersFromText(text)) {
          authorizedNumbers.add(item.value);
        }
      }
    }
  }

  // Also allow numbers that were already present in non-violating baseline sections
  // (e.g. cumulative totals already grounded in executiveSummary/summary), while
  // excluding any number that only appeared as the `observed` error of a blocking violation.
  const forbiddenObservedNumbers = new Set<number>();
  for (const v of priorViolations) {
    if (typeof v.observed === 'string') {
      for (const item of extractUnitNumbersFromText(v.observed)) {
        forbiddenObservedNumbers.add(item.value);
      }
    }
  }
  const baselineUnitNumbers = extractUnitNumbersFromText(reportFullText(baselineReport));
  for (const item of baselineUnitNumbers) {
    if (!forbiddenObservedNumbers.has(item.value)) {
      authorizedNumbers.add(item.value);
    }
  }
  // If a number appeared in `executiveSummary` as cumulative alongside `expected` incremental, keep it authorized.
  if (typeof baselineReport.executiveSummary === 'string') {
    for (const item of extractUnitNumbersFromText(baselineReport.executiveSummary)) {
      authorizedNumbers.add(item.value);
    }
  }

  const resolvedPlanningDocuments: readonly SiteDocument[] = (() => {
    const rawDocs =
      frozenPlanningDocuments && frozenPlanningDocuments.length > 0
        ? frozenPlanningDocuments
        : site?.documents && site.documents.length > 0
          ? site.documents
          : (baselineReport.documents ?? []);
    const reportDocsById = new Map((baselineReport.documents ?? []).map((d) => [d.id, d]));
    const siteDocsById = new Map((site?.documents ?? []).map((d) => [d.id, d]));
    const uploadedById = new Map((sed.uploadedDocuments ?? []).map((u) => [u.docId, u]));
    return rawDocs.map((doc) => {
      const reportDoc = reportDocsById.get(doc.id);
      const siteDoc = siteDocsById.get(doc.id);
      const uploaded = uploadedById.get(doc.id);
      const fullTextCandidate =
        doc.fullExtractedText && doc.fullExtractedText.length > 0
          ? doc.fullExtractedText
          : reportDoc?.fullExtractedText &&
              (!doc.extractedCharsKept || reportDoc.fullExtractedText.length === doc.extractedCharsKept)
            ? reportDoc.fullExtractedText
            : uploaded?.textContent && (!doc.extractedCharsKept || uploaded.textContent.length === doc.extractedCharsKept)
              ? uploaded.textContent
              : doc.textContent && (!doc.extractedCharsKept || doc.textContent.length === doc.extractedCharsKept)
                ? doc.textContent
                : reportDoc?.fullExtractedText ?? uploaded?.textContent ?? doc.textContent;
      const pagesCandidate = doc.pages?.length ? doc.pages : reportDoc?.pages;
      const rawStoragePathCandidate = doc.rawStoragePath ?? siteDoc?.rawStoragePath ?? reportDoc?.rawStoragePath;
      return {
        ...doc,
        ...(fullTextCandidate ? { fullExtractedText: fullTextCandidate } : {}),
        ...(pagesCandidate ? { pages: pagesCandidate } : {}),
        ...(rawStoragePathCandidate ? { rawStoragePath: rawStoragePathCandidate } : {}),
      };
    });
  })();

  const planningById = new Map(resolvedPlanningDocuments.map((d) => [d.id, d]));
  const enrichedUploadedDocuments = (sed.uploadedDocuments ?? []).map((u) => {
    const fullDoc = u.docId ? planningById.get(u.docId) : undefined;
    const fullBody = fullDoc?.fullExtractedText;
    if (fullBody && fullBody.length > (u.textContent?.length ?? 0)) {
      return {
        ...u,
        textContent: fullBody,
        ...(fullDoc?.pages ? { pages: fullDoc.pages } : {}),
      };
    }
    return u;
  });

  const bomSource =
    (baselineReport as { supplyChainPlanAtGen?: { bom?: unknown[] } }).supplyChainPlanAtGen?.bom ??
    (site as { supplyChainPlan?: { bom?: unknown[] } } | null | undefined)?.supplyChainPlan?.bom;
  const frozenBomItems = (Array.isArray(bomSource)
    ? bomSource
        .filter((item): item is { id: string; name: string; quantity: number; derivedFrom?: string; phaseId?: string } => Boolean(item && typeof item === 'object'))
        .map(({ id, name, quantity, derivedFrom, phaseId }) => ({ id, name, quantity, derivedFrom, phaseId }))
    : []) as unknown as Parameters<typeof buildNumericSourceEvidence>[0]['frozenBomItems'];
  const frozenDesignLoadMarginSources = freezeDesignLoadMarginSources(
    resolvedPlanningDocuments,
    sed.fullProjectIntent?.phases,
  );
  const frozenModeledProjectBudgetSource: FrozenModeledProjectBudgetSource | undefined =
    effectiveOwnerUid && sed.id && Number.isInteger(sed.version) && sed.version > 0
      ? {
          ownerUid: effectiveOwnerUid,
          siteId: activeStore.siteId,
          sed: structuredClone(sed),
          documents: structuredClone(resolvedPlanningDocuments),
          expectedSedAuthority: { id: sed.id, version: sed.version },
        }
      : undefined;
  const frozenKnowledge = getAllKnowledgeEntries();
  const frozenSiteContextClaims = liveSiteContextClaims(sed);

  return (targetReport: SiteRagReport): RunGroundingGateResult => {
    const campusAdvisoryProjection =
      targetReport.campusResourceAdvisoryAtGen?.projection ??
      targetReport.siteDiligenceEvidence?.campusResourceAdvisory;
    let campusAdvisoryStatements: readonly string[] | undefined;
    if (campusAdvisoryProjection) {
      try {
        campusAdvisoryStatements = campusResourceAdvisoryNumericStatements(campusAdvisoryProjection);
      } catch {
        campusAdvisoryStatements = undefined;
      }
    }
    const baseGateResult = customRunGate
      ? customRunGate(targetReport)
      : runGroundingGate({
          narrativeText: reportFullText(targetReport),
          store: activeStore,
          mode: 'on',
          frozenBomSubtotalSource: { siteId: activeStore.siteId, sed, report: targetReport },
          numericSourceEvidence: buildNumericSourceEvidence({
            references: targetReport.references,
            documents: enrichedUploadedDocuments,
            knowledge: frozenKnowledge,
            frozenProjectIntent: sed.fullProjectIntent,
            frozenSiteResearch: sed.siteResearch,
            frozenGridEvidence: sed.grid,
            frozenSiteContextClaims,
            frozenSupplyChainSource: { siteId: activeStore.siteId, sed },
            frozenBomItems,
            frozenDesignLoadMarginSources,
            frozenModeledProjectBudgetSource,
            ...(sed.demonstrationProvenance && resolvedPlanningDocuments.length > 0
              ? {
                  frozenGridPlanningSources: {
                    basis: sed.demonstrationProvenance,
                    documents: resolvedPlanningDocuments.filter(
                      (document) => !targetReport.documentIds?.length || targetReport.documentIds.includes(document.id),
                    ),
                  },
                }
              : {}),
            report: targetReport,
            campusAdvisoryStatements,
          }),
          quantitySourceRoles: {
            wetlandAcreageBasis: sed.environmental?.data?.wetlandAcreageBasis,
            wetlandAcres: sed.environmental?.data?.wetlandAcres,
            connectivity: sed.connectivity?.data ?? undefined,
          },
          ...(sed.flood?.data?.areaAnalysis
            ? { floodAreaAnalysis: sed.flood.data.areaAnalysis }
            : {}),
        });

    const normalizedTargetBody = normalizeForLookup(collectReportNarrativeBody(targetReport));
    const mergedViolations: GroundingViolation[] = [...baseGateResult.result.violations];

    // 1. Retain any prior violation whose offending span still survives in the target report.
    // When `observed` is a bare number+unit (e.g. "24 MW" or "20 MW") that legitimately appears
    // elsewhere in the report, anchor survival to `priorV.excerpt` so fixing the offending sentence clears it.
    const isBareNumericObserved = (val: unknown): boolean =>
      typeof val === 'string' &&
      /^\s*[$]?\d+(?:,\d{3})*(?:\.\d+)?(?:\s*[A-Za-z%/°-]+)?\s*$/.test(val);
    for (const priorV of priorViolations) {
      if (priorV.factId && activeStore.facts.has(priorV.factId)) continue;
      if (priorV.assertionRole === 'source-design-load-margin') continue;
      const anchor =
        isBareNumericObserved(priorV.observed) &&
        typeof priorV.excerpt === 'string' &&
        priorV.excerpt.trim().length >= 4
          ? priorV.excerpt
          : (priorV.observed ?? priorV.excerpt);
      if (
        typeof anchor === 'string' &&
        anchor.trim().length >= 4 &&
        appearsInNormalizedBody(normalizedTargetBody, anchor)
      ) {
        const alreadyPresent = mergedViolations.some(
          (v) =>
            v.kind === priorV.kind &&
            v.factId === priorV.factId &&
            v.observed === priorV.observed,
        );
        if (!alreadyPresent) {
          mergedViolations.push(priorV);
        }
      }
    }

    // 2. When evaluating the post-correction candidate, verify every unit-bearing numeric
    // claim in each correction's replacementText is grounded in the SED / canonical store.
    if (targetReport !== baselineReport) {
      const authorizedArray = [...authorizedNumbers];
      for (const c of corrections) {
        const currentFieldVal = readStringAtPath(targetReport, c.fieldPath) ?? '';
        if (!currentFieldVal.includes(c.replacementText)) continue;
        const unitMentions = extractUnitNumbersFromText(c.replacementText);
        for (const mention of unitMentions) {
          const isAuthorized = authorizedArray.some((authNum) =>
            numbersMatchWithinTolerance(mention.value, authNum),
          );
          if (!isAuthorized) {
            const alreadyFlagged = mergedViolations.some(
              (v) =>
                typeof v.observed === 'string' &&
                v.observed.includes(mention.raw),
            );
            if (!alreadyFlagged) {
              mergedViolations.push({
                kind: 'value-mismatch',
                severity: 'block',
                factId: c.anchorFactPaths?.[0] ?? `sed.${c.fieldPath}`,
                observed: mention.raw,
                expected: authorizedArray.length > 0 ? authorizedArray.join(', ') : 'SED-grounded value',
                excerpt: c.replacementText,
                message: `Correction in "${c.fieldPath}" states "${mention.raw}", which is not grounded in the frozen SED.`,
              });
            }
          }
        }
      }
    }

    const blocking = mergedViolations.filter((v) => v.severity === 'block');
    return {
      ...baseGateResult,
      result: {
        ...baseGateResult.result,
        clean: mergedViolations.length === 0,
        violations: mergedViolations,
        blocking,
        stats: {
          ...baseGateResult.result.stats,
          valueMismatches: mergedViolations.filter((v) => v.kind === 'value-mismatch').length,
          inventedNumbers: mergedViolations.filter((v) => v.kind === 'invented-number').length,
          inventedEntities: mergedViolations.filter((v) => v.kind === 'invented-entity').length,
        },
      },
      shouldBlock: blocking.some((v) => v.kind !== 'unlabeled-estimate'),
    };
  };
}

function ensureGroundingModeActive(report: SiteRagReport): SiteRagReport {
  const existingGrounding = report.qaFindings?.grounding;
  const activeMode =
    existingGrounding?.mode === 'warn' || existingGrounding?.mode === 'on'
      ? existingGrounding.mode
      : 'on';
  return {
    ...report,
    qaFindings: {
      ...report.qaFindings,
      grounding: {
        clean: existingGrounding?.clean ?? true,
        violations: existingGrounding?.violations ?? [],
        stats: existingGrounding?.stats ?? {
          numericFacts: 0,
          entityFacts: 0,
          numericMentions: 0,
          entityMentions: 0,
          valueMismatches: 0,
          inventedNumbers: 0,
          inventedEntities: 0,
          inventedEntityHallucinations: 0,
          unlabeledEstimates: 0,
          deadPointers: 0,
          orphanReferences: 0,
        },
        ...existingGrounding,
        mode: activeMode,
      },
    },
  };
}

function blockingKeySet(report: SiteRagReport): Set<string> {
  return new Set(
    collectTruthBlockingRows(report.qaFindings).map(({ detector, row }) =>
      finalTruthRowKey(detector, row as Record<string, unknown>),
    ),
  );
}

function unexemptedAbsenceIdentities(report: SiteRagReport): Set<string> {
  return new Set(
    extractAbsenceAssertionCandidates(report)
      .filter((row) => classifySentenceForTest(row.sentenceText) !== null)
      .map((row) => absenceSentenceIdentity(row.sectionId, row.sentenceText)),
  );
}

/**
 * Apply one or more source-supported audit corrections deterministically,
 * revalidate truth gates, selectively clear resolved holds, and mint a new
 * immutable revision.
 */
export function applySourceSupportedAuditCorrection(
  args: ApplySourceSupportedAuditCorrectionArgs,
): ApplySourceSupportedAuditCorrectionResult {
  const {
    report: rawReport,
    sed,
    corrections: rawCorrections,
    userState,
    siteId,
    ownerUid = 'owner-local',
    siteOwnerUid,
    auditOverrides,
    runGate,
    appliedAt = new Date().toISOString(),
    appliedByUid,
  } = args;

  const report = userState !== undefined ? overlaySanityDecisionMap(rawReport, userState) : rawReport;

  if (!args.revalidateOnly && (!Array.isArray(rawCorrections) || rawCorrections.length === 0)) {
    return { applied: false, reason: 'empty-corrections' };
  }

  const corrections: Array<SourceSupportedAuditCorrectionInput & { rawFieldPath?: string }> = rawCorrections.map((c) => {
    const rawField = typeof c.fieldPath === 'string' && c.fieldPath.trim().length > 0
      ? c.fieldPath.trim()
      : (c.section ?? '').trim();
    const explicitLookupIds = [
      ...(c.sanityFindingIdentity ? [c.sanityFindingIdentity.trim()] : []),
      ...(c.targetSanityFindingMatches ?? []).map((m: string) => m.trim()),
    ].filter(Boolean);
    const matchedSanityForResolution = (report.sanityFindings ?? []).find((sf) => {
      const sfId = sanityFindingIdentity(sf);
      return explicitLookupIds.some(
        (expId) => sfId === expId || sf.id === expId || sfId.toLowerCase() === expId.toLowerCase(),
      );
    });
    const findingTextForResolution =
      matchedSanityForResolution?.finding ?? c.targetSanityFindingMatches?.[0];
    const resolvedField = resolveReportFieldPathForCorrection(
      report,
      rawField,
      findingTextForResolution,
      c.targetSubstring,
    );
    return {
      ...c,
      rawFieldPath: typeof c.fieldPath === 'string' && c.fieldPath.trim().length > 0 ? c.fieldPath.trim() : undefined,
      fieldPath: resolvedField,
    };
  });

  for (const c of corrections) {
    if (c.resolution === 'withdrawn') {
      return {
        applied: false,
        reason: 'correction-withdrawn',
        detail: `Correction for "${c.fieldPath}" has been withdrawn and cannot be applied.`,
      };
    }
    if (isVerifiedSanityCategory(c.category) || c.resolution === 'ignore') {
      return {
        applied: false,
        reason: 'acknowledgement-not-factual-correction',
        detail: `Finding on "${c.fieldPath}" is an acknowledgement or ignored note, not an approved factual correction.`,
      };
    }
    if (c.fieldPath.startsWith('ambiguous:')) {
      const candidates = c.fieldPath.slice('ambiguous:'.length);
      return {
        applied: false,
        reason: 'ambiguous-field-path',
        detail: `Finding references multiple report fields (${candidates}); specify an explicit single fieldPath and target replacement text before applying.`,
      };
    }
    if (c.fieldPath.startsWith('wrong-field:')) {
      const mismatch = c.fieldPath.slice('wrong-field:'.length);
      return {
        applied: false,
        reason: 'wrong-field-mapping',
        detail: `Target field mismatch (${mismatch}): the finding identifies a different report field than the requested section/fieldPath.`,
      };
    }
    if (!areSectionAndFieldPathCompatible(c.section, c.fieldPath)) {
      return {
        applied: false,
        reason: 'wrong-field-mapping',
        detail: `Resolved field path "${c.fieldPath}" does not belong to section "${c.section}".`,
      };
    }
    if (typeof c.evidenceCitation !== 'string' || c.evidenceCitation.trim().length === 0) {
      return {
        applied: false,
        reason: 'missing-evidence-citation',
        detail: `Correction for ${c.fieldPath} must include a non-empty evidenceCitation.`,
      };
    }
    if (isForbiddenFieldPath(c.fieldPath)) {
      return {
        applied: false,
        reason: 'forbidden-field-path',
        detail: `Field path "${c.fieldPath}" is immutable or protected.`,
      };
    }
    if (typeof c.replacementText !== 'string' || c.replacementText.trim().length === 0) {
      return {
        applied: false,
        reason: 'no-effective-change',
        detail: `Replacement text for "${c.fieldPath}" must be non-empty.`,
      };
    }

    // Check if an explicit sanityFindingIdentity or targetSanityFindingMatches refers to a
    // withdrawn correction in the ledger or an acknowledgement/ignored finding on the report.
    const explicitIds = [
      ...(c.sanityFindingIdentity ? [c.sanityFindingIdentity.trim()] : []),
      ...(c.targetSanityFindingMatches ?? []).map((m) => m.trim()),
    ].filter(Boolean);

    const associatedFindingTexts: string[] = [
      ...(c.targetSanityFindingMatches ?? []).map((m) => m.trim()).filter(Boolean),
    ];

    for (const expId of explicitIds) {
      const ledgerEntry = report.approvedFactualCorrections?.entries?.[expId];
      if (ledgerEntry?.status === 'withdrawn') {
        return {
          applied: false,
          reason: 'correction-withdrawn',
          detail: `Correction "${expId}" is marked withdrawn in the durable ledger.`,
        };
      }
      const matchedFinding = (report.sanityFindings ?? []).find((sf) => {
        const sfId = sanityFindingIdentity(sf);
        return sfId === expId || sf.id === expId || sfId.toLowerCase() === expId.toLowerCase() || (sf.finding ?? '').toLowerCase().includes(expId.toLowerCase());
      });
      if (matchedFinding) {
        if (matchedFinding.finding) {
          associatedFindingTexts.push(matchedFinding.finding);
        }
        const matchedId = sanityFindingIdentity(matchedFinding);
        if (
          report.approvedFactualCorrections?.entries?.[matchedId]?.status === 'withdrawn' ||
          matchedFinding.correctionLineage?.status === 'withdrawn'
        ) {
          return {
            applied: false,
            reason: 'correction-withdrawn',
            detail: `Correction "${matchedId}" has been withdrawn.`,
          };
        }
        if (
          (matchedFinding.severity as string) === 'verified' ||
          isVerifiedSanityCategory(matchedFinding.category) ||
          matchedFinding.resolution === 'ignore'
        ) {
          return {
            applied: false,
            reason: 'acknowledgement-not-factual-correction',
            detail: `Finding "${matchedId}" is a verified acknowledgement or ignored item, not a factual correction.`,
          };
        }
      }
      (report.qualityReview?.issues ?? []).forEach((rawIssue, idx) => {
        const rec = (rawIssue ?? {}) as { issue?: unknown; description?: unknown; message?: unknown; section?: unknown };
        const issueText = String(rec.issue ?? rec.description ?? rec.message ?? '').trim();
        if (!issueText) return;
        const syntheticId = `qr-issue-${idx}`;
        const sectionName = typeof rec.section === 'string' && rec.section.trim() ? rec.section.trim() : 'executiveSummary';
        const syntheticIdentity = sanityFindingIdentity({
          id: syntheticId,
          section: sectionName,
          finding: issueText,
        });
        const syntheticInconsistencyIdentity = sanityFindingIdentity({
          id: syntheticId,
          category: 'inconsistency',
          section: sectionName,
          finding: issueText,
        });
        if (
          expId === syntheticId ||
          expId.toLowerCase() === syntheticIdentity.toLowerCase() ||
          expId.toLowerCase() === syntheticInconsistencyIdentity.toLowerCase() ||
          issueText.toLowerCase().includes(expId.toLowerCase())
        ) {
          associatedFindingTexts.push(issueText);
        }
      });
    }

    for (const ft of associatedFindingTexts) {
      const explicitPaths = extractExplicitFieldPathsFromFinding(report, ft);
      const cleanRawFieldPath = (c.rawFieldPath ?? '').trim().replace(/\[(\d+)\]/g, '.$1');
      const hasExplicitSingleCallerLeaf =
        cleanRawFieldPath.length > 0 &&
        !cleanRawFieldPath.includes('/') &&
        (explicitPaths.includes(cleanRawFieldPath) ||
          cleanRawFieldPath === 'executiveSummary' ||
          readStringAtPath(report, cleanRawFieldPath) !== null);
      if (explicitPaths.length > 1 && !hasExplicitSingleCallerLeaf) {
        return {
          applied: false,
          reason: 'ambiguous-field-path',
          detail: `Finding references multiple report fields (${explicitPaths.join(', ')}); specify an explicit single fieldPath and target replacement text before applying.`,
        };
      }
      const normCurrentField = c.fieldPath.replace(/\[(\d+)\]/g, '.$1');
      if (explicitPaths.length === 1 && explicitPaths[0] !== normCurrentField) {
        return {
          applied: false,
          reason: 'wrong-field-mapping',
          detail: `Finding explicitly references "${explicitPaths[0]}", which does not match target field "${c.fieldPath}".`,
        };
      }
    }

    const complaintCheck = isDiagnosticComplaintNotReplacement(c.replacementText, associatedFindingTexts);
    if (complaintCheck.isComplaint) {
      return {
        applied: false,
        reason: 'diagnostic-complaint-not-replacement',
        detail: complaintCheck.detail,
      };
    }
  }

  const resolvedSiteId = siteId ?? report.siteId ?? sed.siteId ?? 'site-unknown';
  const effectiveRunGate = buildSourceBoundGroundingRunner({
    baselineReport: report,
    sed,
    corrections,
    siteId: resolvedSiteId,
    ownerUid,
    siteOwnerUid,
    site: args.site,
    frozenPlanningDocuments: args.frozenPlanningDocuments,
    appliedAt,
    customRunGate: runGate,
  });

  // Baseline truth measurement on the original report with mandatory source-bound grounding gate.
  const baselineReportWithGrounding = ensureGroundingModeActive(report);
  const baselineMeasured = measureFinalTruth(baselineReportWithGrounding, {
    sed,
    reportType: report.reportType ?? 'feasibility',
    preProjection: baselineReportWithGrounding,
    priorReport: { ...baselineReportWithGrounding, qaFindings: { absenceAssertionAudit: baselineReportWithGrounding.qaFindings?.absenceAssertionAudit, caveatHonesty: baselineReportWithGrounding.qaFindings?.caveatHonesty?.downgraded ? { downgraded: baselineReportWithGrounding.qaFindings.caveatHonesty.downgraded } as never : undefined } },
    auditOverrides,
    runGate: effectiveRunGate,
    recheckSource: true,
  }).report;
  const baselineBlockingKeys = blockingKeySet(baselineMeasured);
  const baselineAbsenceIds = unexemptedAbsenceIdentities(report);

  const candidate = structuredClone(report);
  const appliedCorrectionReceipts: SourceSupportedAuditCorrectionReceiptV1['corrections'] = [];
  const previousRevisionId = report.reportRevision?.revisionId;
  const claimedSanityIdentities = new Set<string>();
  const pendingLedgerAdditions: Array<{
    identity: string;
    findingForLedger: SanityFinding;
    userNote?: string;
    applyDirective?: SanityFinding['applyDirective'];
    approvedByUid?: string;
    approvedAt: string;
    originReportId: string;
    directive: string;
  }> = [];

  for (const c of corrections) {
    const currentText = readStringAtPath(candidate, c.fieldPath);
    if (currentText === null) {
      return {
        applied: false,
        reason: 'target-field-not-found',
        detail: `String field "${c.fieldPath}" was not found on report.`,
      };
    }
    let nextText: string;
    if (typeof c.targetSubstring === 'string' && c.targetSubstring.length > 0) {
      if (!currentText.includes(c.targetSubstring)) {
        return {
          applied: false,
          reason: 'target-substring-not-found',
          detail: `Target substring "${c.targetSubstring}" not found in "${c.fieldPath}".`,
        };
      }
      nextText = currentText.replaceAll(c.targetSubstring, c.replacementText);
    } else {
      nextText = c.replacementText;
    }

    if (nextText === currentText) {
      return {
        applied: false,
        reason: 'no-effective-change',
        detail: `Correction on "${c.fieldPath}" produced no change.`,
      };
    }

    // Fail closed if the replacement text itself introduces an unexempted absence assertion
    // that was not present in the original field text.
    const beforeAbsenceTrigger = classifySentenceForTest(currentText);
    const afterAbsenceTrigger = classifySentenceForTest(c.replacementText);
    if (afterAbsenceTrigger !== null && afterAbsenceTrigger !== beforeAbsenceTrigger) {
      return {
        applied: false,
        reason: 'introduces-new-absence-assertion',
        detail: `Replacement text in "${c.fieldPath}" introduces an unverified absence assertion ("${afterAbsenceTrigger}").`,
      };
    }

    // Check if any matching sanityFinding on the report has been withdrawn in the ledger.
    const matchedSanityFinding = (candidate.sanityFindings ?? []).find((sf) => {
      if ((sf.severity as string) === 'verified' || isVerifiedSanityCategory(sf.category)) return false;
      const sfId = sanityFindingIdentity(sf);
      if (claimedSanityIdentities.has(sfId)) return false;
      if (c.sanityFindingIdentity && (sfId === c.sanityFindingIdentity || sf.id === c.sanityFindingIdentity)) return true;
      const lowerId = sfId.toLowerCase();
      const lowerRawId = (sf.id ?? '').toLowerCase();
      const lowerText = (sf.finding ?? '').toLowerCase();
      if (
        (c.targetSanityFindingMatches ?? []).some((m) => {
          const needle = m.trim().toLowerCase();
          return needle.length > 0 && (lowerId === needle || lowerRawId === needle || lowerText.includes(needle));
        })
      ) {
        return true;
      }
      if (
        c.targetSubstring &&
        c.targetSubstring.trim().length >= 4 &&
        appearsInNormalizedBody(normalizeForLookup(sf.finding ?? ''), c.targetSubstring.trim())
      ) {
        return true;
      }
      return false;
    });

    if (matchedSanityFinding) {
      const matchedId = sanityFindingIdentity(matchedSanityFinding);
      if (
        candidate.approvedFactualCorrections?.entries?.[matchedId]?.status === 'withdrawn' ||
        matchedSanityFinding.correctionLineage?.status === 'withdrawn'
      ) {
        return {
          applied: false,
          reason: 'correction-withdrawn',
          detail: `Correction "${matchedId}" has been withdrawn and cannot be applied.`,
        };
      }
      if (matchedSanityFinding.resolution === 'ignore') {
        return {
          applied: false,
          reason: 'acknowledgement-not-factual-correction',
          detail: `Finding "${matchedId}" was ignored and is not an approved factual correction.`,
        };
      }
    }

    // Verify source binding (SED identity, [Ref N], anchorFactPaths, evidenceSourceFields, evidenceSourceIds, and concrete source anchor).
    const bindingCheck = verifyCorrectionSourceBinding(c, sed, report, matchedSanityFinding);
    if (!bindingCheck.ok) {
      return {
        applied: false,
        reason: 'unverified-source-evidence',
        detail: bindingCheck.detail,
      };
    }

    if (!writeStringAtPath(candidate, c.fieldPath, nextText)) {
      return {
        applied: false,
        reason: 'target-field-not-found',
        detail: `Failed to write string field "${c.fieldPath}".`,
      };
    }

    if (
      c.fieldPath === 'executiveSummary' &&
      typeof candidate.summary === 'string' &&
      !corrections.some((other) => other.fieldPath === 'summary')
    ) {
      if (candidate.summary === currentText) {
        candidate.summary = nextText;
      } else if (c.targetSubstring && candidate.summary.includes(c.targetSubstring)) {
        candidate.summary = candidate.summary.replaceAll(c.targetSubstring, c.replacementText);
      }
    } else if (
      c.fieldPath === 'summary' &&
      typeof candidate.executiveSummary === 'string' &&
      !corrections.some((other) => other.fieldPath === 'executiveSummary')
    ) {
      if (candidate.executiveSummary === currentText) {
        candidate.executiveSummary = nextText;
      } else if (c.targetSubstring && candidate.executiveSummary.includes(c.targetSubstring)) {
        candidate.executiveSummary = candidate.executiveSummary.replaceAll(c.targetSubstring, c.replacementText);
      }
    }

    updateClaimsMatchingEdit(candidate, currentText, nextText, c.targetSubstring, c.replacementText);

    appliedCorrectionReceipts.push({
      fieldPath: c.fieldPath,
      evidenceCitation: c.evidenceCitation.trim(),
      beforeHash: hashCanonicalJson(currentText),
      afterHash: hashCanonicalJson(nextText),
    });

    const anchorFactValues = c.anchorFactPaths?.length
      ? extractFactValuesFromSed(sed, c.anchorFactPaths)
      : undefined;
    const mergedSourceFields =
      anchorFactValues || c.evidenceSourceFields
        ? {
            ...(anchorFactValues ?? {}),
            ...(c.evidenceSourceFields ?? {}),
          }
        : matchedSanityFinding?.evidenceSourceFields;
    const mergedSourceIds = c.evidenceSourceIds
      ? [...c.evidenceSourceIds]
      : matchedSanityFinding?.evidenceSourceIds;
    const mergedSourceRevision =
      c.evidenceSourceRevision ?? matchedSanityFinding?.evidenceSourceRevision ?? sed.version;

    const findingForLedger: SanityFinding = matchedSanityFinding
      ? {
          ...matchedSanityFinding,
          section: c.section ?? matchedSanityFinding.section ?? c.fieldPath,
          category: matchedSanityFinding.category ?? 'inconsistency',
          suggestion: c.replacementText,
          evidence: mergedSourceFields
            ? `${c.evidenceCitation.trim()} | ${JSON.stringify(mergedSourceFields)}`
            : c.evidenceCitation.trim(),
          ...(mergedSourceFields ? { evidenceSourceFields: mergedSourceFields } : {}),
          ...(mergedSourceIds ? { evidenceSourceIds: mergedSourceIds } : {}),
          evidenceSourceRevision: mergedSourceRevision,
          source: c.evidenceCitation.trim(),
        }
      : {
          id: c.sanityFindingIdentity?.trim() || `correction-${c.fieldPath}`,
          severity: 'medium',
          section: c.section ?? c.fieldPath,
          category: 'inconsistency',
          finding: c.targetSubstring
            ? `Corrected "${c.targetSubstring}" in ${c.fieldPath}`
            : `Corrected ${c.fieldPath}`,
          suggestion: c.replacementText,
          evidence: mergedSourceFields
            ? `${c.evidenceCitation.trim()} | ${JSON.stringify(mergedSourceFields)}`
            : c.evidenceCitation.trim(),
          ...(mergedSourceFields ? { evidenceSourceFields: mergedSourceFields } : {}),
          ...(mergedSourceIds ? { evidenceSourceIds: mergedSourceIds } : {}),
          evidenceSourceRevision: mergedSourceRevision,
          source: c.evidenceCitation.trim(),
        };

    const identity = matchedSanityFinding
      ? sanityFindingIdentity(matchedSanityFinding)
      : (c.sanityFindingIdentity?.trim() || sanityFindingIdentity(findingForLedger));

    if (candidate.approvedFactualCorrections?.entries?.[identity]?.status === 'withdrawn') {
      return {
        applied: false,
        reason: 'correction-withdrawn',
        detail: `Correction "${identity}" is marked withdrawn in the durable ledger.`,
      };
    }

    if (matchedSanityFinding) {
      claimedSanityIdentities.add(sanityFindingIdentity(matchedSanityFinding));
    }

    pendingLedgerAdditions.push({
      identity,
      findingForLedger,
      userNote: c.userNote ?? matchedSanityFinding?.userNote,
      applyDirective: matchedSanityFinding?.applyDirective,
      approvedByUid: appliedByUid ?? matchedSanityFinding?.resolvedByUid,
      approvedAt: matchedSanityFinding?.resolvedAt ?? appliedAt,
      originReportId: matchedSanityFinding?.correctionLineage?.originReportId ?? candidate.id,
      directive: c.userNote?.trim() || c.replacementText,
    });
  }

  const resolvedPlanningDocsForRepair: readonly SiteDocument[] = (() => {
    const rawDocs =
      args.frozenPlanningDocuments && args.frozenPlanningDocuments.length > 0
        ? args.frozenPlanningDocuments
        : args.site?.documents && args.site.documents.length > 0
          ? args.site.documents
          : (candidate.documents ?? []);
    const reportDocsById = new Map((candidate.documents ?? []).map((d) => [d.id, d]));
    const siteDocsById = new Map((args.site?.documents ?? []).map((d) => [d.id, d]));
    const uploadedById = new Map((sed.uploadedDocuments ?? []).map((u) => [u.docId, u]));
    return rawDocs.map((doc) => {
      const reportDoc = reportDocsById.get(doc.id);
      const siteDoc = siteDocsById.get(doc.id);
      const uploaded = uploadedById.get(doc.id);
      const fullTextCandidate =
        doc.fullExtractedText && doc.fullExtractedText.length > 0
          ? doc.fullExtractedText
          : reportDoc?.fullExtractedText &&
              (!doc.extractedCharsKept || reportDoc.fullExtractedText.length === doc.extractedCharsKept)
            ? reportDoc.fullExtractedText
            : uploaded?.textContent && (!doc.extractedCharsKept || uploaded.textContent.length === doc.extractedCharsKept)
              ? uploaded.textContent
              : doc.textContent && (!doc.extractedCharsKept || doc.textContent.length === doc.extractedCharsKept)
                ? doc.textContent
                : reportDoc?.fullExtractedText ?? uploaded?.textContent ?? doc.textContent;
      const pagesCandidate = doc.pages?.length ? doc.pages : reportDoc?.pages;
      const rawStoragePathCandidate = doc.rawStoragePath ?? siteDoc?.rawStoragePath ?? reportDoc?.rawStoragePath;
      return {
        ...doc,
        ...(fullTextCandidate ? { fullExtractedText: fullTextCandidate } : {}),
        ...(pagesCandidate ? { pages: pagesCandidate } : {}),
        ...(rawStoragePathCandidate ? { rawStoragePath: rawStoragePathCandidate } : {}),
      };
    });
  })();
  const planningRepairCtx = {
    sed,
    references: candidate.references ?? [],
    reportType: candidate.reportType ?? 'feasibility',
    siteId: siteId ?? candidate.siteId ?? sed.siteId,
    projectIntent: candidate.projectIntentAtGen ?? args.site?.projectIntent ?? sed.fullProjectIntent,
    reportCreatedAt: candidate.createdAt,
    frozenPlanningDocuments: resolvedPlanningDocsForRepair,
  };
  for (const field of sourceRoleReviewFields(candidate)) {
    const repaired =
      buildSourceRoleSentenceRepair(field.text, sed, candidate.references ?? [], field.fieldPath) ??
      buildSourceRolePlanningRepair(field.text, planningRepairCtx, field.fieldPath);
    if (repaired && repaired !== field.text && writeStringAtPath(candidate, field.fieldPath, repaired)) {
      if (field.fieldPath === 'executiveSummary' && candidate.summary === field.text) {
        candidate.summary = repaired;
      } else if (field.fieldPath === 'summary' && candidate.executiveSummary === field.text) {
        candidate.executiveSummary = repaired;
      }
      updateClaimsMatchingEdit(candidate, field.text, repaired);
    }
  }
  reconcileReportVerdictProseWithConclusion(candidate);

  if (Array.isArray(candidate.claims) && candidate.claims.length > 0) {
    const narrativeSections = collectNarrativeSections(candidate);
    const normalizedNarrativeForClaims = normalizeForLookup(
      collectReportNarrativeBody({ ...candidate, claims: [], editorialClaims: [] } as unknown as SiteRagReport),
    );
    const filteredClaims = candidate.claims.filter((c) => {
      if (
        isContextOnlyQueueClaim(c.field ?? '', c.value, c.reportSectionId ?? '', narrativeSections) ||
        isNonHeadroomGridCapacityClaim(c.field ?? '', c.value, c.reportSectionId ?? '', narrativeSections)
      ) {
        return false;
      }
      const rawClaimText =
        typeof (c as { text?: unknown }).text === 'string' ? (c as { text: string }).text.trim() : '';
      const rawClaimVal = typeof c.value === 'string' ? c.value.trim() : '';
      for (const val of [rawClaimText, rawClaimVal]) {
        if (val.length >= 8) {
          const wasTargeted = corrections.some(
            (corr) =>
              corr.targetSubstring &&
              appearsInNormalizedBody(normalizeForLookup(corr.targetSubstring), val),
          );
          if ((wasTargeted || val.length >= 25) && !appearsInNormalizedBody(normalizedNarrativeForClaims, val)) {
            return false;
          }
        }
      }
      return true;
    });
    if (filteredClaims.length !== candidate.claims.length) {
      candidate.claims = filteredClaims;
    }
    const isCseWarning = (w: unknown): boolean => {
      const rec = (w ?? {}) as { type?: string; step?: string };
      return rec.type === 'cse-internal-conflict' || rec.step === 'cse-internal-conflict';
    };
    if (
      Array.isArray(candidate.qualityWarnings) &&
      candidate.qualityWarnings.some(isCseWarning)
    ) {
      const remainingConflicts = detectClaimConflicts(candidate.claims);
      const nonCseWarnings = candidate.qualityWarnings.filter((w) => !isCseWarning(w));
      if (remainingConflicts.length > 0) {
        candidate.qualityWarnings = [
          ...nonCseWarnings,
          {
            type: 'cse-internal-conflict',
            count: remainingConflicts.length,
            conflicts: remainingConflicts,
          } as unknown as NonNullable<SiteRagReport['qualityWarnings']>[number],
        ];
      } else if (nonCseWarnings.length > 0) {
        candidate.qualityWarnings = nonCseWarnings;
      } else {
        delete candidate.qualityWarnings;
      }
    }
  }
  if (Array.isArray(candidate.qualityWarnings) && candidate.qualityWarnings.length > 0) {
    const candidateBodyForWarnings = collectReportNarrativeBody(candidate);
    const nextWarnings = candidate.qualityWarnings.filter((w) => {
      const rec = (w ?? {}) as { type?: string; samples?: Array<{ phrase?: string }> };
      if (rec.type === 'rnh-stance-mismatch' && Array.isArray(rec.samples) && rec.samples.length > 0) {
        return rec.samples.some((s) => typeof s.phrase === 'string' && s.phrase.length > 0 && candidateBodyForWarnings.includes(s.phrase));
      }
      if (rec.type === 'wpg-capacity-drift') {
        return false;
      }
      return true;
    });
    if (nextWarnings.length === 0) {
      delete candidate.qualityWarnings;
    } else if (nextWarnings.length !== candidate.qualityWarnings.length) {
      candidate.qualityWarnings = nextWarnings;
    }
  }

  // Re-run deterministic truth gates (`internalNumberFindings`, `caveatHonesty`,
  // `waterUseScreening`, `sourceRoleTruth`, and mandatory source-bound `grounding` gate).
  const { report: markerCorrectedCandidate } = correctUnsupportedVerifiedMarkers(candidate);
  const caveatNormalizedCandidate = applyCaveatHonestyGate(markerCorrectedCandidate, sed, {
    normalizeTier3Blocks: true,
    maxNormalizePasses: 3,
  });
  const candidateForTruth = ensureGroundingModeActive(pruneResolvedAbsenceRows(caveatNormalizedCandidate));
  const remeasured = measureFinalTruth(candidateForTruth, {
    sed,
    reportType: candidateForTruth.reportType ?? 'feasibility',
    preProjection: candidateForTruth,
    priorReport: { ...candidateForTruth, qaFindings: { absenceAssertionAudit: candidateForTruth.qaFindings?.absenceAssertionAudit, caveatHonesty: candidateForTruth.qaFindings?.caveatHonesty?.downgraded ? { downgraded: candidateForTruth.qaFindings.caveatHonesty.downgraded } as never : undefined } },
    auditOverrides,
    runGate: effectiveRunGate,
    recheckSource: true,
  }).report;
  if (remeasured.factCheck || args.revalidateOnly) {
    remeasured.factCheck = runFrozenReportFactCheck(remeasured, sed);
  }

  // Reject if the correction introduces any NEW blocking truth violation against the SED.
  const afterBlockingRows = collectTruthBlockingRows(remeasured.qaFindings);
  const newViolationRows = afterBlockingRows.filter(
    ({ detector, row }) => !baselineBlockingKeys.has(finalTruthRowKey(detector, row as Record<string, unknown>)),
  );
  if (newViolationRows.length > 0) {
    const summary = newViolationRows
      .slice(0, 15)
      .map(({ detector, row }) => {
        const r = row as Record<string, unknown>;
        return `\n[${detector}] kind=${String(r.kind)} obs=${String(r.observed ?? r.factId)} excerpt=${String(r.excerpt ?? r.claimText ?? r.sentenceText ?? r.message)}`;
      })
      .join(' | ');
    return {
      applied: false,
      reason: 'introduces-new-truth-violation',
      detail: `Correction introduced ${newViolationRows.length} new truth violation(s) against the SED: ${summary}`,
    };
  }

  // Reject if the correction introduces a new unexempted absence assertion.
  const afterAbsenceIds = unexemptedAbsenceIdentities(remeasured);
  const newAbsenceIds = [...afterAbsenceIds].filter((id) => !baselineAbsenceIds.has(id));
  if (newAbsenceIds.length > 0) {
    return {
      applied: false,
      reason: 'introduces-new-absence-assertion',
      detail: `Correction introduced ${newAbsenceIds.length} new unverified absence assertion(s).`,
    };
  }

  // Revalidate sanityFindings AFTER truth remeasurement: only clear a non-verified
  // sanityFinding when its offending token/substring is no longer present in the
  // narrative body AND no blocking truth violation remains on that span.
  const rawBaselineBody = collectReportNarrativeBody(report);
  const normalizedBaselineBody = normalizeForLookup(rawBaselineBody);
  const rawCandidateBody = collectReportNarrativeBody(remeasured);
  const normalizedCandidateBody = normalizeForLookup(rawCandidateBody);
  const hasOutOfScopeWaterFindings = (remeasured.qaFindings?.waterUseScreening?.findings ?? []).some(
    (f) => f.kind === 'water-band-out-of-scope-load',
  );
  const hasUnsupportedSiteWaterFindings = (remeasured.qaFindings?.waterUseScreening?.findings ?? []).some(
    (f) => f.kind === 'unsupported-site-water-value',
  );
  if (remeasured.qualityReview && Array.isArray(remeasured.qualityReview.issues) && remeasured.qualityReview.issues.length > 0) {
    const explicitMatches = new Set(
      corrections.flatMap((c) => [
        ...(c.sanityFindingIdentity ? [c.sanityFindingIdentity.trim().toLowerCase()] : []),
        ...(c.targetSanityFindingMatches ?? []).map((m) => m.trim().toLowerCase()),
      ]).filter(Boolean),
    );
    const indexedIssues = remeasured.qualityReview.issues.map((issue, origIdx) => ({ issue, origIdx }));
    const baseSurviving = args.revalidateOnly
      ? remeasured.qualityReview.issues.filter((issue) => {
          const rec = (issue ?? {}) as { issue?: unknown; description?: unknown; message?: unknown };
          const text = String(rec.issue ?? rec.description ?? rec.message ?? '');
          return !isReviewIssueAlreadyQualifiedInText(text, rawCandidateBody, remeasured);
        })
      : filterSurvivingReviewIssues(
          remeasured.qualityReview.issues,
          rawCandidateBody,
          remeasured,
        ).surviving;
    const baseSurvivingSet = new Set(baseSurviving);
    const surviving = indexedIssues
      .filter(({ issue, origIdx }) => {
        if (!baseSurvivingSet.has(issue)) return false;
        const rec = (issue ?? {}) as { issue?: unknown; description?: unknown; message?: unknown; section?: unknown };
        const text = String(rec.issue ?? rec.description ?? rec.message ?? '').trim();
        if (isFalsePositiveQueueDurationFindingText(text, [rawCandidateBody])) return false;
        if (/water-use screen check flagged out-of-scope load bindings/i.test(text) && !hasOutOfScopeWaterFindings) {
          return false;
        }
        if (
          (/water-use screen check flagged unsupported or unattributed site water values/i.test(text) ||
            /\bsource-stated\s+(?:planning\s+)?(?:site\s+)?water(?:\s+allocation|\s+and\s+wastewater)\b[\s\S]*\boriginating\s+document\s+citation\b/i.test(text)) &&
          !hasUnsupportedSiteWaterFindings
        ) {
          return false;
        }
        if (!args.revalidateOnly) {
          const syntheticId = `qr-issue-${origIdx}`.toLowerCase();
          const sectionName = typeof rec.section === 'string' && rec.section.trim() ? rec.section.trim() : 'executiveSummary';
          const syntheticIdentity = sanityFindingIdentity({
            id: `qr-issue-${origIdx}`,
            section: sectionName,
            finding: text,
          }).toLowerCase();
          const syntheticInconsistencyIdentity = sanityFindingIdentity({
            id: `qr-issue-${origIdx}`,
            category: 'inconsistency',
            section: sectionName,
            finding: text,
          }).toLowerCase();
          const lowerText = text.toLowerCase();
          const targetedByCorrection = corrections.find((c) => {
            const cIds = [
              ...(c.sanityFindingIdentity ? [c.sanityFindingIdentity.trim().toLowerCase()] : []),
              ...(c.targetSanityFindingMatches ?? []).map((m) => m.trim().toLowerCase()),
            ].filter(Boolean);
            return cIds.some(
              (m) =>
                m === syntheticId ||
                m === syntheticIdentity ||
                m === syntheticInconsistencyIdentity ||
                (lowerText.length > 0 && lowerText.includes(m)),
            );
          });
          if (targetedByCorrection && explicitMatches.size > 0) {
            const updatedFieldText = readStringAtPath(remeasured, targetedByCorrection.fieldPath) ?? '';
            const baselineFieldText = readStringAtPath(report, targetedByCorrection.fieldPath) ?? '';
            if (
              /\bwithout\s+hedging\s+it\s+as\s+an?\s+unverified\b/i.test(text) ||
              isPhaseCumulativeAdditionComplaint(text)
            ) {
              if (isReviewIssueAlreadyQualifiedInText(text, updatedFieldText, remeasured)) {
                return false;
              }
              return true;
            }
            const preContrast = text.split(/\b(?:instead of|rather than|whereas|which contradicts|contradicting|however)\b/i)[0] ?? text;
            const quotedSpan =
              extractOffendingToken(text) ??
              preContrast.match(/['"“‘]([^'"”’]{4,400})['"”’]/)?.[1] ??
              null;
            const quotedWasInBaselineField = Boolean(
              quotedSpan && appearsInNormalizedBody(normalizeForLookup(baselineFieldText), quotedSpan),
            );
            const quotedStillInUpdatedField = Boolean(
              quotedSpan && appearsInNormalizedBody(normalizeForLookup(updatedFieldText), quotedSpan),
            );
            const unreplacedFieldText =
              targetedByCorrection.replacementText &&
              targetedByCorrection.replacementText.trim() !== (targetedByCorrection.targetSubstring ?? '').trim()
                ? updatedFieldText.replaceAll(targetedByCorrection.replacementText, '')
                : updatedFieldText;
            const targetSubStillInField = Boolean(
              targetedByCorrection.targetSubstring &&
                targetedByCorrection.targetSubstring.trim().length >= 4 &&
                appearsInNormalizedBody(normalizeForLookup(unreplacedFieldText), targetedByCorrection.targetSubstring.trim()),
            );
            const hasExplicitEradicatedTargetSub = Boolean(
              targetedByCorrection.targetSubstring &&
                targetedByCorrection.targetSubstring.trim().length >= 4 &&
                appearsInNormalizedBody(
                  normalizeForLookup(baselineFieldText),
                  targetedByCorrection.targetSubstring.trim(),
                ) &&
                !targetSubStillInField &&
                newViolationRows.length === 0,
            );
            const isResolvedWaterOrGroundingQualificationIssue =
              /\bwater-use\s+screen\b|\bsource-stated\s+water\s+and\s+wastewater\s+quantities\b/i.test(text) &&
              (remeasured.qaFindings?.waterUseScreening?.findings ?? []).length === 0 &&
              (remeasured.qaFindings?.grounding?.violations ?? []).filter((v) => v.severity === 'block').length === 0;
            if (
              !targetSubStillInField &&
              (hasExplicitEradicatedTargetSub ||
                isResolvedWaterOrGroundingQualificationIssue ||
                !quotedWasInBaselineField ||
                !quotedStillInUpdatedField)
            ) {
              return false;
            }
          }
        }
        return true;
      })
      .map(({ issue }) => issue);
    if (
      surviving.length !== remeasured.qualityReview.issues.length ||
      remeasured.qualityReview.issueCount !== surviving.length ||
      (surviving.length === 0 && remeasured.qualityReview.verdict === 'needs-revision')
    ) {
      remeasured.qualityReview = {
        ...remeasured.qualityReview,
        issues: surviving,
        issueCount: surviving.length,
        ...(surviving.length === 0 && remeasured.qualityReview.verdict === "needs-revision"
          ? { verdict: "pass" as const }
          : {}),
      };
    }
  }
  if (Array.isArray(remeasured.sanityFindings) && remeasured.sanityFindings.length > 0) {
    const explicitMatches = new Set(
      corrections.flatMap((c) => [
        ...(c.sanityFindingIdentity ? [c.sanityFindingIdentity.trim().toLowerCase()] : []),
        ...(c.targetSanityFindingMatches ?? []).map((m) => m.trim().toLowerCase()),
      ]).filter(Boolean),
    );
    const eradicatedSubstrings = corrections
      .map((c) => c.targetSubstring?.trim())
      .filter((sub): sub is string => Boolean(sub && sub.length >= 4 && !appearsInNormalizedBody(normalizedCandidateBody, sub)));
    const survivingSubstrings = corrections
      .map((c) => c.targetSubstring?.trim())
      .filter((sub): sub is string => Boolean(sub && sub.length >= 4 && appearsInNormalizedBody(normalizedCandidateBody, sub)));

    const hadRv7Findings = remeasured.sanityFindings.some((sf) => (sf.id ?? '').startsWith('rv7.'));
    const freshRv7Findings = runReportSanityCheckPass({
      report: remeasured,
      intent: remeasured.projectIntentAtGen ?? args.site?.projectIntent ?? sed.fullProjectIntent,
    });
    const freshRv7ById = new Map(freshRv7Findings.map((f) => [f.id, f]));
    const freshRv7Ids = new Set(freshRv7Findings.map((f) => f.id));

    const filteredSanityFindings = remeasured.sanityFindings
      .filter((sf) => {
        if ((sf.severity as string) === 'verified' || isVerifiedSanityCategory(sf.category)) return true;
        if ((sf.id ?? '').startsWith('rv7.') && !freshRv7Ids.has(sf.id ?? '')) return false;
      const findingText = sf.finding ?? '';
      if (isFalsePositiveQueueDurationFindingText(findingText, [rawCandidateBody])) return false;
      if (isReviewIssueAlreadyQualifiedInText(findingText, rawCandidateBody, remeasured)) return false;
      if (
        /\bwithout\s+hedging\s+it\s+as\s+an?\s+unverified\b/i.test(findingText) ||
        isPhaseCumulativeAdditionComplaint(findingText)
      ) {
        return true;
      }
      if (args.revalidateOnly) return true;
      const isStructuredVsNarrativeFinding = /\bstructured\s+fields\s+state\b[\s\S]*\bwhereas\b/i.test(findingText);
      const contrastParts = findingText.split(/\b(?:instead of|rather than|whereas|which contradicts|contradicting|however)\b/i);
      const narrativeContrastSide = isStructuredVsNarrativeFinding
        ? (contrastParts[1] ?? findingText)
        : (contrastParts[0] ?? findingText);
      const quotedSpan = isStructuredVsNarrativeFinding
        ? ([...narrativeContrastSide.matchAll(/['"“‘]([^'"”’]{15,400})['"”’]/g)].map((m) => m[1]!).sort((a, b) => b.length - a.length)[0] ?? null)
        : (narrativeContrastSide.match(/['"“‘]([^'"”’]{4,400})['"”’]/)?.[1] ?? null);
      const token = isStructuredVsNarrativeFinding ? quotedSpan : (extractOffendingToken(findingText) ?? quotedSpan);
      const tokenWasInBaseline = Boolean(token && appearsInNormalizedBody(normalizedBaselineBody, token));
      const id = sanityFindingIdentity(sf).toLowerCase();
      const rawId = (sf.id ?? '').toLowerCase();
      const text = (sf.finding ?? '').toLowerCase();
      const isExplicitlyMatched = [...explicitMatches].some((m) => id === m || rawId === m || text.includes(m));
      const matchedByEradicatedTargetSub =
        isExplicitlyMatched &&
        newViolationRows.length === 0 &&
        corrections.some((c) => {
          const cMatches = [
            ...(c.sanityFindingIdentity ? [c.sanityFindingIdentity.trim().toLowerCase()] : []),
            ...(c.targetSanityFindingMatches ?? []).map((m) => m.trim().toLowerCase()),
          ].filter(Boolean);
          if (!cMatches.some((m) => id === m || rawId === m || text.includes(m))) return false;
          if (!c.targetSubstring || c.targetSubstring.trim().length < 4) return false;
          const updatedFieldText = readStringAtPath(remeasured, c.fieldPath) ?? '';
          const isDirectIdMatch = cMatches.some((m) => id === m || rawId === m);
          const removedFromTargetField = !appearsInNormalizedBody(
            normalizeForLookup(updatedFieldText),
            c.targetSubstring.trim(),
          );
          if (!removedFromTargetField) return false;
          return isDirectIdMatch || !appearsInNormalizedBody(normalizedCandidateBody, c.targetSubstring.trim());
        });
      if (matchedByEradicatedTargetSub) {
        return false;
      }
      // If the finding quotes a specific offending token/span or targetSubstring that still survives in the narrative body,
      // never clear it even if the caller explicitly targeted its identity.
      if (token && appearsInNormalizedBody(normalizedCandidateBody, token)) {
        return true;
      }
      if (survivingSubstrings.some((sub) => appearsInNormalizedBody(normalizeForLookup(findingText), sub))) {
        return true;
      }
      if (isExplicitlyMatched) {
        return false;
      }
      // Never drop an untargeted queued 'apply' finding during an unrelated section's correction.
      if (sf.resolution === 'apply') {
        return true;
      }
      if (eradicatedSubstrings.some((sub) => appearsInNormalizedBody(normalizeForLookup(sf.finding ?? ''), sub))) {
        return false;
      }
      if (token && tokenWasInBaseline && !appearsInNormalizedBody(normalizedCandidateBody, token)) {
        return false;
      }
      return true;
    })
    .map((sf) => {
      const fresh = sf.id ? freshRv7ById.get(sf.id) : undefined;
      if (!fresh) return sf;
      return {
        ...sf,
        section: fresh.section,
        severity: fresh.severity,
        category: fresh.category,
        finding: fresh.finding,
        ...(fresh.suggestion !== undefined ? { suggestion: fresh.suggestion } : {}),
      };
    });
    const survivingIds = new Set(filteredSanityFindings.map((sf) => sf.id));
    remeasured.sanityFindings = hadRv7Findings
      ? [
          ...filteredSanityFindings,
          ...freshRv7Findings.filter((f) => !survivingIds.has(f.id)),
        ]
      : filteredSanityFindings;
  }

  // Record durable approved factual corrections in the versioned ledger only for findings
  // that passed source-bound revalidation (and whose offending span does not survive).
  const survivingSanityIds = new Set<string>(
    (remeasured.sanityFindings ?? []).map((sf) => sanityFindingIdentity(sf)),
  );
  (remeasured.qualityReview?.issues ?? []).forEach((rawIssue, idx) => {
    const rec = (rawIssue ?? {}) as { issue?: unknown; description?: unknown; message?: unknown; section?: unknown };
    const issueText = String(rec.issue ?? rec.description ?? rec.message ?? '').trim();
    if (!issueText) return;
    const sectionName = typeof rec.section === 'string' && rec.section.trim() ? rec.section.trim() : 'executiveSummary';
    survivingSanityIds.add(`qr-issue-${idx}`);
    survivingSanityIds.add(
      sanityFindingIdentity({
        id: `qr-issue-${idx}`,
        section: sectionName,
        finding: issueText,
      }),
    );
    survivingSanityIds.add(
      sanityFindingIdentity({
        id: `qr-issue-${idx}`,
        category: 'inconsistency',
        section: sectionName,
        finding: issueText,
      }),
    );
  });
  const resolvedSanityItems: Array<{
    identity: string;
    section: string;
    finding: string;
    directive: string;
    status: 'resolved';
  }> = [];

  for (const item of pendingLedgerAdditions) {
    if (survivingSanityIds.has(item.identity)) {
      continue;
    }
    remeasured.approvedFactualCorrections = upsertApprovedFactualCorrection(
      remeasured.approvedFactualCorrections,
      {
        identity: item.identity,
        finding: item.findingForLedger,
        userNote: item.userNote,
        applyDirective: item.applyDirective,
        approvedByUid: item.approvedByUid,
        approvedAt: item.approvedAt,
        originReportId: item.originReportId,
        lastEvaluationStatus: 'resolved',
        lastEvaluatedAt: appliedAt,
        lastAppliedReportId: remeasured.id,
      },
    );
    resolvedSanityItems.push({
      identity: item.identity,
      section: item.findingForLedger.section,
      finding: item.findingForLedger.finding,
      directive: item.directive,
      status: 'resolved',
    });
  }

  // Reconcile any pre-existing active ledger entries and appliedCorrectionsSummary items against
  // current survivingSanityIds so ledger status and Audit/decision readiness never disagree.
  let entriesChanged = false;
  if (remeasured.approvedFactualCorrections?.entries) {
    const targetedLedgerIdentities = new Set(pendingLedgerAdditions.map((item) => item.identity));
    const updatedEntries = { ...remeasured.approvedFactualCorrections.entries };
    for (const [entryKey, entry] of Object.entries(updatedEntries)) {
      if (!entry || entry.status !== 'active') continue;
      // Preserve queued-for-rerun instructions (`lastEvaluationStatus === 'queued'`) verbatim
      // when they have not been applied to a report (`!entry.lastAppliedReportId`) and were
      // not targeted by the current correction.
      if (
        entry.lastEvaluationStatus === 'queued' &&
        !entry.lastAppliedReportId &&
        !targetedLedgerIdentities.has(entryKey)
      ) {
        continue;
      }
      const nextStatus = (
        survivingSanityIds.has(entryKey) ? 'persisting' : 'resolved'
      ) as unknown as NonNullable<typeof entry.lastEvaluationStatus>;
      if (entry.lastEvaluationStatus !== nextStatus) {
        updatedEntries[entryKey] = {
          ...entry,
          lastEvaluationStatus: nextStatus,
          lastEvaluatedAt: appliedAt,
        };
        entriesChanged = true;
      }
    }
    if (entriesChanged) {
      remeasured.approvedFactualCorrections = {
        ...remeasured.approvedFactualCorrections,
        updatedAt: appliedAt,
        entries: updatedEntries,
      };
    }
  }

  // Recompute truthQuestions and selective qualityHold clearance.
  const attested = attestedCheckIds(auditOverrides, remeasured.reportType);
  const baselineTruthQuestions = buildTruthQuestions(baselineMeasured.qaFindings, attested);
  const nextTruthQuestions = buildTruthQuestions(remeasured.qaFindings, attested);
  const recomputedHold = resolveQualityHold(remeasured);

  // Also check if any prior stamped `qualityHoldFindings` were not part of `qaFindings`
  // (e.g. legacy stamps) and still survive in the updated narrative body.
  const priorStamped = report.qualityHoldFindings ?? [];
  const baselineMeasuredBlockingSet = new Set(
    summarizeQaSeverity(baselineMeasured.qaFindings).blocking.map((v) => `${v.detector}: ${v.message}`),
  );
  const baselineBlockingSet = new Set([
    ...priorStamped,
    ...baselineMeasuredBlockingSet,
    ...summarizeQaSeverity(report.qaFindings).blocking.map((v) => `${v.detector}: ${v.message}`),
  ]);
  const uncappedBlockingSet = new Set([
    ...(recomputedHold?.findings ?? []),
    ...summarizeQaSeverity(remeasured.qaFindings).blocking.map((v) => `${v.detector}: ${v.message}`),
  ]);
  const reevaluatedPriorStamped = reevaluateStampedHoldFindings(remeasured, priorStamped).findings.filter(
    (stamp) => {
      if (/^(?:grounding|internalNumberFindings|caveatHonesty|waterUseScreening|sourceRoleTruth):/.test(stamp)) {
        return uncappedBlockingSet.has(stamp);
      }
      if (/^qualityReview:/i.test(stamp)) {
        if (uncappedBlockingSet.has(stamp)) return true;
        const bodyAfterPrefix = stamp.replace(/^qualityReview:\s*/i, '').trim();
        return (remeasured.qualityReview?.issues ?? []).some((iss) => {
          const txt =
            typeof (iss as unknown) === 'string'
              ? (iss as unknown as string).trim()
              : String(
                  (iss as { issue?: unknown; description?: unknown; message?: unknown })?.issue ??
                    (iss as { description?: unknown })?.description ??
                    (iss as { message?: unknown })?.message ??
                    '',
                ).trim();
          return Boolean(txt && (txt === bodyAfterPrefix || stamp.includes(txt)));
        });
      }
      if (/^sanity:/i.test(stamp)) {
        if (uncappedBlockingSet.has(stamp)) return true;
        const bodyAfterPrefix = stamp.replace(/^sanity:\s*/i, '').trim();
        return (remeasured.sanityFindings ?? []).some((sf) => {
          if ((sf.severity as string) === 'verified' || isVerifiedSanityCategory(sf.category)) {
            return false;
          }
          const txt = (sf.finding ?? '').trim();
          return Boolean(txt && (txt === bodyAfterPrefix || stamp.includes(txt)));
        });
      }
      const quoted = stamp.match(/Asserted absence without evidence:\s*"([^"]+)"/i)?.[1]
        ?? stamp.match(/Narrative asserts?\s*"([^"]+)"/i)?.[1]
        ?? stamp.match(/Narrative states?\s*"([^"]+)"/i)?.[1]
        ?? extractOffendingToken(stamp);
      if (quoted) {
        return appearsInNormalizedBody(normalizedCandidateBody, quoted);
      }
      if (/^absenceAssertionAudit:/.test(stamp)) {
        return uncappedBlockingSet.has(stamp);
      }
      return true;
    },
  );

  const blockingSetUnchanged =
    priorStamped.length > 0 &&
    reevaluatedPriorStamped.length === priorStamped.length &&
    [...uncappedBlockingSet].every((stamp) => baselineBlockingSet.has(stamp));
  const remainingHoldFindings = blockingSetUnchanged
    ? [...priorStamped]
    : Array.from(new Set([...(recomputedHold?.findings ?? []), ...reevaluatedPriorStamped]));
  const clearedHoldFindings = priorStamped.filter(
    (stamp) => !remainingHoldFindings.includes(stamp),
  );

  const nextSedMaterialDependencies =
    remeasured.sedMaterialDependenciesAtGen !== undefined
      ? buildReportSedMaterialDependencyManifest(sed, remeasured.reportType ?? 'feasibility', {
          fullDocument: remeasured.sedMaterialDependenciesAtGen.scope === 'full-document',
        })
      : undefined;

  const nextQaFindings = {
    ...remeasured.qaFindings,
    truthQuestions: nextTruthQuestions,
  };

  const nextReport: SiteRagReport = {
    ...remeasured,
    siteId: resolvedSiteId,
    sedId: sed.id,
    sedVersionUsed: sed.version,
    ...(nextSedMaterialDependencies
      ? { sedMaterialDependenciesAtGen: nextSedMaterialDependencies }
      : {}),
    minorVersion: report.minorVersion ?? 0,
    updatedAt: report.updatedAt ?? appliedAt,
    qaFindings: nextQaFindings,
  };

  if (remainingHoldFindings.length > 0) {
    nextReport.qualityHold = 'needs-revision';
    nextReport.qualityHoldFindings = remainingHoldFindings;
  } else {
    delete nextReport.qualityHold;
    delete nextReport.qualityHoldFindings;
  }
  delete nextReport.analysisReadiness;
  delete nextReport.analysisRevision;

  if (
    (nextReport.reportType ?? 'feasibility') === 'feasibility' &&
    (nextReport.screeningRecommendationSetV1 !== undefined || nextReport.projectIntentAtGen !== undefined)
  ) {
    nextReport.screeningRecommendationSetV1 = deriveScreeningRecommendationSetV1(
      nextReport,
      nextReport.siteId,
    );
  }
  reconcileReportVerdictProseWithConclusion(nextReport);

  // Populate appliedCorrectionsSummary BEFORE freezing `sourceReport` so that
  // `sourceReport`, `document`, `presentation`, and `bodyReport` all derive
  // from the exact same canonical report state.
  let appliedCorrectionsSummaryChanged = false;
  if (
    resolvedSanityItems.length > 0 ||
    (nextReport.appliedCorrectionsSummary?.items?.length ?? 0) > 0
  ) {
    const priorItems = (nextReport.appliedCorrectionsSummary?.items ?? [])
      .filter((item) => !resolvedSanityItems.some((r) => r.identity === item.identity))
      .map((item) => {
        if (item.status === 'superseded') return item;
        const nextStatus: 'resolved' | 'persisting' = survivingSanityIds.has(item.identity)
          ? 'persisting'
          : 'resolved';
        return item.status === nextStatus ? item : { ...item, status: nextStatus };
      });
    const allItems = [...priorItems, ...resolvedSanityItems];
    const itemsUnchangedFromBaseline =
      resolvedSanityItems.length === 0 &&
      report.appliedCorrectionsSummary !== undefined &&
      hashCanonicalJson(allItems) ===
        hashCanonicalJson(report.appliedCorrectionsSummary.items ?? []);
    appliedCorrectionsSummaryChanged = !itemsUnchangedFromBaseline;
    nextReport.appliedCorrectionsSummary = {
      sourceReportId: report.id,
      evaluatedAt: itemsUnchangedFromBaseline
        ? report.appliedCorrectionsSummary!.evaluatedAt
        : appliedAt,
      requestedCount: allItems.length,
      resolvedCount: allItems.filter((i) => i.status === 'resolved').length,
      persistingCount: allItems.filter((i) => i.status === 'persisting').length,
      supersededCount: allItems.filter((i) => i.status === 'superseded').length,
      items: allItems,
    };
  }

  if (
    Array.isArray(nextReport.references) &&
    nextReport.references.length > 0 &&
    deduplicateCanonicalReferences(nextReport.references).references.length < nextReport.references.length
  ) {
    const renumbered = renumberReportReferences(nextReport);
    Object.assign(nextReport, renumbered);
  }

  const stripFrozenDerivedFields = (input: SiteRagReport): SiteRagReport => {
    const copy: SiteRagReport = { ...input };
    delete copy.reportRevision;
    delete copy.reportRevisionId;
    delete copy.storagePath;
    delete copy.decisionAnalysis;
    delete copy.analysisRevision;
    delete copy.analysisReadiness;
    delete copy.pdfDelivery;
    delete (copy as unknown as Record<string, unknown>).sourcePath;
    delete (copy as unknown as Record<string, unknown>).sourceHash;
    delete (copy as unknown as Record<string, unknown>).documentPath;
    delete (copy as unknown as Record<string, unknown>).presentationPath;
    return cleanJson(copy);
  };

  const baselineSourceReport = stripFrozenDerivedFields(rawReport);
  const candidateSourceReport = stripFrozenDerivedFields(nextReport);

  const isZeroDiffRevalidate =
    args.revalidateOnly === true &&
    appliedCorrectionReceipts.length === 0 &&
    clearedHoldFindings.length === 0 &&
    !entriesChanged &&
    !appliedCorrectionsSummaryChanged &&
    resolvedSanityItems.length === 0 &&
    rawReport.sedId === sed.id &&
    rawReport.sedVersionUsed === sed.version &&
    Boolean(rawReport.qualityHold) === (remainingHoldFindings.length > 0) &&
    hashCanonicalJson(remainingHoldFindings) === hashCanonicalJson(rawReport.qualityHoldFindings ?? []) &&
    Array.isArray(rawReport.qaFindings?.truthQuestions) &&
    hashCanonicalJson(nextTruthQuestions) === hashCanonicalJson(rawReport.qaFindings.truthQuestions) &&
    hashCanonicalJson(remeasured.qualityReview ?? null) ===
      hashCanonicalJson(rawReport.qualityReview ?? null) &&
    hashCanonicalJson(remeasured.sanityFindings ?? []) ===
      hashCanonicalJson(rawReport.sanityFindings ?? []) &&
    hashCanonicalJson(nextQaFindings) === hashCanonicalJson(rawReport.qaFindings ?? null) &&
    hashCanonicalJson(nextSedMaterialDependencies ?? null) ===
      hashCanonicalJson(rawReport.sedMaterialDependenciesAtGen ?? null) &&
    hashCanonicalJson(candidateSourceReport) === hashCanonicalJson(baselineSourceReport);

  if (!isZeroDiffRevalidate) {
    nextReport.minorVersion = (report.minorVersion ?? 0) + 1;
    nextReport.updatedAt = appliedAt;
  }

  // Build canonical frozen source payload matching `finalizeReportRevision.ts` and `loadStagedPrintInputs.ts`:
  // strip derived revision/delivery pointers so `hashCanonicalJson(sourceReport) === reportRevision.sourceHash`.
  const sourceReport = isZeroDiffRevalidate
    ? candidateSourceReport
    : stripFrozenDerivedFields(nextReport);
  const sourceHash = hashCanonicalJson(sourceReport);

  // Mint new immutable ReportDocumentModelV1, PresentationSnapshotV1, and DecisionAnalysisSnapshotV1 from `sourceReport`.
  const document = buildReportDocumentModelV1(sourceReport);
  const presentation = buildPresentationSnapshotV1(sourceReport);
  const decisionAnalysis = buildDecisionAnalysisFromReportV1({
    siteId: resolvedSiteId,
    report: sourceReport,
    document,
    sed,
    createdAt: appliedAt,
    projectIntent: sourceReport.projectIntentAtGen ?? null,
    typedPreferences: sourceReport.typedPreferencesAtGen ?? sourceReport.typedPreferences ?? null,
    powerStrategy:
      (sourceReport.typedPreferencesAtGen ?? sourceReport.typedPreferences)?.['power-availability']
        ?.btmStrategy ?? null,
    testFitAuthorityRef: sourceReport.testFitAuthorityRef ?? null,
    upstream: sourceReport.decisionAnalysisUpstreamAtGen ?? [],
  });
  const resolvedSiteOwnerUid = siteOwnerUid ?? ownerUid;
  const analysisPath =
    `users/${resolvedSiteOwnerUid}/sites/${resolvedSiteId}` +
    `/decision-analyses/${decisionAnalysis.domain}/revisions/${decisionAnalysis.contentHash}.json`;
  const analysisRevision = buildAnalysisRevisionReferenceV1({
    snapshot: decisionAnalysis,
    snapshotPath: analysisPath,
    currentInput: decisionAnalysis.input,
    evaluatedAt: appliedAt,
  });

  const revisionPrefix =
    `users/${ownerUid}/reports/${sourceReport.id}/revisions/${document.revisionId}/`;
  const provisionalReportRevision = reportRevisionReferenceV1Schema.parse({
    schemaVersion: 'report-revision-ref-v1',
    revisionId: document.revisionId,
    sourcePath: `${revisionPrefix}source/${sourceHash}.json`,
    sourceHash,
    documentPath: `${revisionPrefix}document/${document.contentHash}.json`,
    documentHash: document.contentHash,
    presentationPath: `${revisionPrefix}presentation/${presentation.presentationHash}.json`,
    presentationHash: presentation.presentationHash,
    artifacts: {},
    deliveryDecision: {
      schemaVersion: 'delivery-decision-v1',
      state: 'ready',
      canViewPrivate: true,
      canExport: true,
      canPublish: true,
      reasons: [],
    },
  });
  const semanticDeliveryDecision = evaluateReportDelivery({
    report: {
      ...sourceReport,
      reportRevision: provisionalReportRevision,
    },
    bodyState: 'ready',
    authorization: 'authorized',
    document,
    presentation,
  });
  const reportRevision = {
    ...(reportRevisionReferenceV1Schema.parse({
      ...provisionalReportRevision,
      deliveryDecision: semanticDeliveryDecision,
    }) as unknown as ReportRevisionReferenceV1),
    sourcePath: `${revisionPrefix}source/${sourceHash}.json`,
    sourceHash,
  };

  const bodyReport = cleanJson({
    ...sourceReport,
    decisionAnalysis,
    analysisRevision,
    analysisReadiness: analysisRevision.readiness,
    reportRevision,
    pdfDelivery: { status: 'not-requested', retryable: false },
  }) as SiteRagReport;
  const bodyHash = hashCanonicalJson(bodyReport);
  const bodyPath = `${revisionPrefix}body/${bodyHash}.json`;

  const receipt: SourceSupportedAuditCorrectionReceiptV1 = {
    schemaVersion: 1,
    appliedAt,
    ...(appliedByUid ? { appliedByUid } : {}),
    sedId: sed.id,
    sedVersion: sed.version,
    ...(previousRevisionId ? { previousRevisionId } : {}),
    newRevisionId: document.revisionId,
    corrections: appliedCorrectionReceipts,
    clearedHoldFindings,
    remainingHoldFindings,
  };

  nextReport.decisionAnalysis = decisionAnalysis;
  nextReport.analysisRevision = analysisRevision;
  nextReport.analysisReadiness = analysisRevision.readiness;
  nextReport.reportRevision = reportRevision;
  nextReport.reportRevisionId = reportRevision.revisionId;
  nextReport.storagePath = bodyPath;
  nextReport.pdfDelivery = { status: 'not-requested', retryable: false };

  return {
    applied: true,
    ...(isZeroDiffRevalidate ? { noOpRevalidate: true } : {}),
    report: nextReport,
    sourceReport,
    bodyReport,
    bodyHash,
    bodyPath,
    document,
    presentation,
    reportRevision,
    decisionAnalysis,
    analysisRevision,
    previousRevisionId,
    newRevisionId: isZeroDiffRevalidate ? (previousRevisionId ?? document.revisionId) : document.revisionId,
    clearedHoldFindings,
    remainingHoldFindings,
    receipt,
  };
}

export function revalidateAuditReportAgainstSed(
  args: Omit<ApplySourceSupportedAuditCorrectionArgs, 'corrections'>,
): ApplySourceSupportedAuditCorrectionResult {
  return applySourceSupportedAuditCorrection({
    ...args,
    corrections: [],
    revalidateOnly: true,
  });
}

export interface SourceSupportedAuditCorrectionPersistenceAdapter {
  writeImmutableArtifact: (path: string, payload: unknown) => Promise<void> | void;
  saveReport: (
    report: SiteRagReport,
    receipt: SourceSupportedAuditCorrectionReceiptV1,
  ) => Promise<void> | void;
}

/**
 * Persists a newly minted immutable report revision and analysis revision from
 * `applySourceSupportedAuditCorrection` without overwriting prior revision paths.
 *
 * Writes the exact hashed `sourceReport` to `sourcePath`, plus `documentPath`,
 * `presentationPath`, `analysisRevision.snapshotPath`, and `bodyPath`, and
 * publishes the revision references on `saveReport` ONLY after all immutable
 * artifact writes have succeeded.
 */
export async function persistSourceSupportedAuditCorrectionRevision(
  result: Extract<ApplySourceSupportedAuditCorrectionResult, { applied: true }>,
  adapter: SourceSupportedAuditCorrectionPersistenceAdapter,
): Promise<{
  savedRevisionId: string;
  writtenPaths: string[];
}> {
  if (result.noOpRevalidate === true) {
    return {
      savedRevisionId: result.newRevisionId,
      writtenPaths: [],
    };
  }
  const writtenPaths: string[] = [];
  const write = async (path: string, payload: unknown) => {
    await adapter.writeImmutableArtifact(path, payload);
    writtenPaths.push(path);
  };

  await write(result.reportRevision.sourcePath, result.sourceReport);
  await write(result.reportRevision.documentPath, result.document);
  await write(result.reportRevision.presentationPath, result.presentation);
  await write(result.analysisRevision.snapshotPath, result.decisionAnalysis);
  await write(result.bodyPath, result.bodyReport);

  const publishedReport: SiteRagReport = {
    ...result.report,
    reportRevision: result.reportRevision,
    reportRevisionId: result.newRevisionId,
    storagePath: result.bodyPath,
    analysisRevision: result.analysisRevision,
    analysisReadiness: result.analysisRevision.readiness,
  };
  await adapter.saveReport(publishedReport, result.receipt);

  return {
    savedRevisionId: result.newRevisionId,
    writtenPaths,
  };
}
